import Phaser from 'phaser';
import {
  GRID_COLS,
  GRID_ROWS,
  CELL_SIZE,
  CELL_GAP,
  GAME_WIDTH,
  GAME_HEIGHT,
  FAMILY_COLORS,
  FAMILY_LABELS,
  Tier,
  TIER_POINTS,
  TIER_NAMES,
  MERGE_TWEEN_DURATION,
  SPAWN_TWEEN_DURATION,
  DROP_TWEEN_DURATION,
  SPLINTER_TWEEN_DURATION,
  SPAWN_DELAY_MS,
  CHAIN_ANIMATION_DELAY_MS,
  chainStepMultiplier,
  Family,
  SPAWN_COUNT_THRESHOLDS,
  DECAY_WARNING_RATIO,
  MAX_ACTIVE_FAMILIES_PER_RUN,
  SPLINTER_OVERCHARGE_COST,
  MAX_OVERCHARGE,
} from '../config';
import { Grid } from '../systems/Grid';
import { MergeEngine } from '../systems/MergeEngine';
import { EnergySystem } from '../systems/EnergySystem';
import { OverchargeSystem } from '../systems/OverchargeSystem';
import { IdleSystem } from '../systems/IdleSystem';
import { FamilyProgressionManager } from '../systems/FamilyProgressionManager';
import { SpawnSystem } from '../systems/SpawnSystem';
import { computeIdleStagger } from '../systems/idleStagger';
import { GameOverDetector } from '../systems/GameOverDetector';
import { DecaySystem } from '../systems/DecaySystem';
import { ChainEngine } from '../systems/ChainEngine';
import { UidAllocator } from '../systems/UidAllocator';
import { CodexManager } from '../ui/CodexManager';
import { ProgressManager } from '../ui/ProgressManager';
import { ShopManager } from '../ui/ShopManager';
import { SaveManager } from '../ui/SaveManager';
import { DraftOverlay } from '../ui/dom/DraftOverlay';
import { GameOverOverlay } from '../ui/dom/GameOverOverlay';
import { DiscoveryModalOverlay } from '../ui/dom/DiscoveryModalOverlay';
import { MergeToast } from '../ui/dom/MergeToastOverlay';
import { Haptics } from '../ui/Haptics';
import { PauseOverlay } from '../ui/dom/PauseOverlay';
import {
  ACCENT_CYAN,
  ACCENT_PURPLE,
  ACCENT_GOLD_CSS,
  ACCENT_CYAN_CSS,
  TEXT_PRIMARY_CSS,
  TEXT_SECONDARY_CSS,
  TEXT_TERTIARY_CSS,
  FONT_DISPLAY,
  FONT_MONO,
} from '../ui/theme';
import {
  ElementInstance,
  getElementDefOrThrow,
  getRandomFragment,
  getMergeResult,
  ELEMENT_DEFS,
} from '../data/elements';
import {
  Modifier,
  ModifierId,
  pickRunModifiers,
  getModifiersByIds,
  hasModifier,
  MODIFIER_VOLATILE_THRESHOLD_DELTA,
  MODIFIER_FRENZY_SPAWN_BONUS,
  MODIFIER_PRIME_TIME_MULTIPLIER,
  MODIFIER_CASCADE_SURGE_OVERCHARGE_BONUS,
} from '../data/modifiers';

/**
 * GameScene: Main gameplay scene with grid-based merging, drag-and-drop,
 * splinter mechanics, energy system, and idle generation.
 */
export class GameScene extends Phaser.Scene {
  // Game systems
  private grid!: Grid;
  private mergeEngine!: MergeEngine;
  private energySystem!: EnergySystem;
  private overchargeSystem!: OverchargeSystem;
  private idleSystem!: IdleSystem;
  private familyProgression!: FamilyProgressionManager;
  private spawnSystem!: SpawnSystem;
  private gameOverDetector!: GameOverDetector;
  private decaySystem!: DecaySystem;
  private chainEngine!: ChainEngine;
  private codexManager!: CodexManager;
  private progressManager!: ProgressManager;
  private shopManager!: ShopManager;

  // DOM overlays — created on demand, torn down on scene shutdown.
  private draftOverlay: DraftOverlay | null = null;
  private gameOverOverlay: GameOverOverlay | null = null;
  private pauseOverlay: PauseOverlay | null = null;
  /** CRT-style scanline layered above the canvas. See design/handoff §Game. */
  private scanlineEl: HTMLDivElement | null = null;

  // Sprite management
  private sprites: Map<number, Phaser.GameObjects.Sprite> = new Map();
  private cellBackgrounds: Phaser.GameObjects.Image[][] = [];
  /**
   * Idle-float tween registry, keyed by element uid. We own the tween so we
   * can pause/resume it around positional mutations (drag, merge, cascade) —
   * letting Phaser manage it via `killTweensOf` mid-merge would drop the
   * yoyo baseline and the sprite would settle offset from its cell.
   */
  private idleTweens: Map<number, Phaser.Tweens.Tween> = new Map();

  // Game state
  private score: number = 0;
  private highScore: number = 0;
  private runMerges: number = 0;
  private longestChain: number = 0;
  private runsCompleted: number = 0;

  // Per-run modifiers (threats). Empty during the tutorial and until the first
  // post-tutorial fresh-run rolls a pool. Persists across reloads mid-run.
  private activeModifiers: Modifier[] = [];
  private splinterUsedThisRun: boolean = false;

  // HUD elements for the active-modifier badge row. Created at HUD build time,
  // populated/hidden by updateModifierBadges().
  private modifierBadges: Array<{ bg: Phaser.GameObjects.Image; label: Phaser.GameObjects.Text }> = [];
  private isAnimating: boolean = false;
  private dragStartCol: number = -1;
  private dragStartRow: number = -1;
  private selectedSprite: Phaser.GameObjects.Sprite | null = null;
  private isDragging: boolean = false;
  private dragStartTime: number = 0;
  private uids!: UidAllocator;

  // Splinter long-press state
  private splinterTimer: Phaser.Time.TimerEvent | null = null;
  private splinterGlow: Phaser.GameObjects.Graphics | null = null;
  private splinterTarget: ElementInstance | null = null;

  // Corruption/decay visual overlays (keyed by element UID)
  private corruptionOverlays: Map<number, Phaser.GameObjects.Graphics> = new Map();
  private warningOverlays: Map<number, Phaser.GameObjects.Graphics> = new Map();
  private corruptionToastShown: boolean = false;

  // Discovery toast queue — serialize rapid-fire toasts (e.g. cascade chains
  // surfacing multiple new discoveries) so they don't stack on top of each
  // other at the same screen position.
  private discoveryToastQueue: Array<{ name: string; lore: string; familyColor: number }> = [];
  private discoveryToastActive: boolean = false;

  // Screen-edge corruption FX (designer brief §4 — "make corruption feel dangerous")
  private corruptionVignette: Phaser.FX.Vignette | null = null;
  private corruptionEdgeGlow: Phaser.GameObjects.Graphics | null = null;
  private lastCorruptedCount: number = 0;

  // Overcharge feedback state (designer brief §5 — explosive ready reaction)
  private wasOverchargeReady: boolean = false;

  // Drag selection visual feedback
  private dragHighlight: Phaser.GameObjects.Graphics | null = null;

  // Tutorial state
  // Steps: 0=not started, 1=first merge, 2=second merge, 3=tier-up merge,
  // 4=cascade demo, 5=splinter demo, 6=complete (free play).
  private tutorialStep: number = 0;
  private tutorialOverlay: Phaser.GameObjects.Graphics | null = null;
  private tutorialGlows: Phaser.GameObjects.Graphics[] = [];
  private tutorialArrow: Phaser.GameObjects.Graphics | null = null;
  private tutorialText: Phaser.GameObjects.Text | null = null;
  private tutorialSubText: Phaser.GameObjects.Text | null = null;
  private tutorialSpotlights: Phaser.GameObjects.Graphics[] = [];
  private tutorialWelcomeDismissed: boolean = false;
  // Guardrails — when a tutorial step is active, only these UIDs may be
  // dragged/dropped/splintered. An empty allowed set means "no merges allowed
  // at all this step" (e.g. while a cascade is animating). When the splinter
  // UID is null during steps 1–4, splinter is fully blocked.
  private tutorialAllowedSourceUids: Set<number> = new Set();
  private tutorialAllowedTargetUids: Set<number> = new Set();
  private tutorialAllowedSplinterUid: number | null = null;
  private tutorialOverchargeHighlight: Phaser.GameObjects.Graphics | null = null;

  // Layout — pushed below the new two-row frosted HUD + overcharge bar.
  private gridOffsetX: number = 0;
  private gridOffsetY: number = 176;

  // HUD — top bar (pause / timer / combo / score)
  private scoreText!: Phaser.GameObjects.Text;
  private timerText!: Phaser.GameObjects.Text;
  private comboChipBg!: Phaser.GameObjects.Image;
  private comboChipText!: Phaser.GameObjects.Text;
  private hintText!: Phaser.GameObjects.Text;
  private corruptedText!: Phaser.GameObjects.Text;

  // Objective strip
  private objectiveText!: Phaser.GameObjects.Text;

  // Meters row — energy + overcharge + NEXT-3 preview
  private energyBarBg!: Phaser.GameObjects.Image;
  private energyBarFill!: Phaser.GameObjects.Graphics;
  private energyBarLabel!: Phaser.GameObjects.Text;
  private overchargeBarBg!: Phaser.GameObjects.Image;
  private overchargeBarFill!: Phaser.GameObjects.Graphics;
  private overchargeLabel!: Phaser.GameObjects.Text;
  private nextBarBg!: Phaser.GameObjects.Image;
  private nextLabel!: Phaser.GameObjects.Text;
  private nextPreviewIcons: Phaser.GameObjects.Image[] = [];

  // Bottom action bar — shards / SPLINTER / settings
  private shardsText!: Phaser.GameObjects.Text;
  private splinterBtnBg!: Phaser.GameObjects.Image;
  private splinterBtnGlow!: Phaser.GameObjects.Image | null;
  private splinterBtnLabel!: Phaser.GameObjects.Text;
  private splinterBtnZone!: Phaser.GameObjects.Zone;

  // Timer / combo state
  private runStartMs: number = 0;
  /** Total ms the run has been paused for — subtracted from elapsed time. */
  private pausedElapsedMs: number = 0;
  /** When the current pause started, in `performance.now()`-equivalent ms. */
  private pauseStartedAt: number | null = null;
  /** Latest chain step in flight. Drives the combo chip; resets when the chain completes. */
  private activeChainStep: number = 0;
  /** True while the PauseOverlay is up. Gates spawn/idle timers + drag input. */
  private isPaused: boolean = false;

  // HUD redraw cache.
  //
  // updateHUD() runs every frame. Phaser's Text.setText() unconditionally
  // re-rasterises the string into a Canvas2D and uploads the result as a
  // WebGL texture even when the value hasn't changed — that's ~50–80ms per
  // text per frame on a real device. With five HUD texts and 60fps targets,
  // that was capping the renderer at ~2fps and making every animation /
  // tween / fade-in feel like it was loading slowly. We now cache the last
  // rendered string for each text and only call setText() on a change.
  // Same idea for the overcharge bar geometry and the corruption FX.
  private lastScoreText: string = '';
  private lastTimerText: string = '';
  private lastComboStep: number = 0;
  private lastObjectiveText: string = '';
  private lastEnergyFillW: number = -1;
  private lastHintText: string = '';
  private lastCorruptedText: string = '';
  private lastCorruptedVisible: boolean = false;
  private lastOverchargeFillW: number = -1;
  private lastOverchargeReady: boolean = false;
  private lastNextPreviewKey: string = '';
  private lastShardsText: string = '';
  private lastSplinterReady: boolean = false;
  private lastFXCorruptedCount: number = -1;

  // Auto-save handlers (stored for cleanup)
  private boundVisibilityHandler: (() => void) | null = null;
  private boundBeforeUnloadHandler: (() => void) | null = null;

  // Coalesced-save state (issue #26). A full cascade chain previously issued
  // 5–6 localStorage writes inside 100 ms; we now trail-debounce them and only
  // force a flush at critical boundaries (tab hide, scene shutdown, game over).
  private pendingSaveTimer: Phaser.Time.TimerEvent | null = null;
  private hasPendingSave: boolean = false;
  private static readonly SAVE_DEBOUNCE_MS = 500;

  // Tracks whether we've already warned the player about a quota-exceeded
  // write so the toast doesn't re-fire on every subsequent save.
  private saveQuotaWarned: boolean = false;

  constructor() {
    super({ key: 'Game' });
  }

  create(): void {
    // Reset state from previous scene lifecycle (Phaser reuses scene instances)
    this.sprites.clear();
    this.corruptionOverlays.clear();
    this.warningOverlays.clear();
    this.corruptionToastShown = localStorage.getItem('splintr_corruption_toast') === 'done';
    this.cellBackgrounds = [];
    this.score = 0;
    this.highScore = 0;
    this.runMerges = 0;
    this.longestChain = 0;
    this.runsCompleted = 0;
    this.activeModifiers = [];
    this.splinterUsedThisRun = false;
    this.modifierBadges = [];
    this.isAnimating = false;
    this.isDragging = false;
    this.selectedSprite = null;
    this.splinterTimer = null;
    this.splinterGlow = null;
    this.splinterTarget = null;
    this.dragHighlight = null;
    this.tutorialStep = 0;
    this.tutorialOverlay = null;
    this.tutorialGlows = [];
    this.tutorialArrow = null;
    this.tutorialText = null;
    this.tutorialSubText = null;
    this.tutorialSpotlights = [];
    this.tutorialWelcomeDismissed = false;
    this.tutorialAllowedSourceUids = new Set();
    this.tutorialAllowedTargetUids = new Set();
    this.tutorialAllowedSplinterUid = null;
    this.tutorialOverchargeHighlight = null;

    // Require 8px of movement before a drag starts (prevents accidental drag during splinter hold)
    this.input.dragDistanceThreshold = 8;

    this.mountScanline();

    // Calculate grid centering
    const gridWidth =
      GRID_COLS * (CELL_SIZE + CELL_GAP) - CELL_GAP;
    this.gridOffsetX = (GAME_WIDTH - gridWidth) / 2;

    // Reset HUD redraw caches so first updateHUD() unconditionally writes.
    this.lastScoreText = '';
    this.lastTimerText = '';
    this.lastComboStep = 0;
    this.lastObjectiveText = '';
    this.lastEnergyFillW = -1;
    this.lastHintText = '';
    this.lastCorruptedText = '';
    this.lastCorruptedVisible = false;
    this.lastOverchargeFillW = -1;
    this.lastOverchargeReady = false;
    this.lastNextPreviewKey = '';
    this.lastShardsText = '';
    this.lastSplinterReady = false;
    this.lastFXCorruptedCount = -1;
    this.runStartMs = performance.now();
    this.pausedElapsedMs = 0;
    this.pauseStartedAt = null;
    this.activeChainStep = 0;
    this.isPaused = false;
    this.nextPreviewIcons = [];

    // Initialize systems. One shared UidAllocator prevents the collisions
    // tracked in issue #22 — MergeEngine, IdleSystem, and GameScene.spawnElement
    // all mint from the same monotonically-increasing sequence.
    this.uids = new UidAllocator(1);
    this.grid = new Grid();
    this.codexManager = new CodexManager();
    this.mergeEngine = new MergeEngine(
      this.grid,
      (defId) => this.codexManager.isDiscovered(defId),
      this.uids
    );
    this.energySystem = new EnergySystem();
    this.overchargeSystem = new OverchargeSystem();
    this.idleSystem = new IdleSystem(this.grid, this.uids);
    this.progressManager = new ProgressManager();
    this.shopManager = new ShopManager();
    this.familyProgression = new FamilyProgressionManager();
    this.spawnSystem = new SpawnSystem(this.familyProgression);
    this.gameOverDetector = new GameOverDetector(this.grid);
    this.decaySystem = new DecaySystem(this.grid);
    this.chainEngine = new ChainEngine(this.grid, this.mergeEngine);

    // Wire achievement unlock rewards
    this.progressManager.onAchievementUnlocked = (ach) => {
      this.shopManager.addShards(ach.rewardShards);
      this.shopManager.addCoins(ach.rewardCoins);
    };

    // Set up energy change callback
    this.energySystem.setOnChange(() => this.updateHUD());
    this.overchargeSystem.setOnChange(() => this.updateHUD());

    // Seed the ready-state tracker to the *current* overcharge so that the
    // first updateHUD() call doesn't mis-interpret the starting charge as a
    // fresh 0→1 transition and fire the explosive burst on scene mount.
    this.wasOverchargeReady = this.overchargeSystem.canSplinter();

    // Install the corruption post-FX (designer brief §4 — magenta vignette that
    // creeps in as the board accumulates corrupted blockers). Phaser's
    // built-in Vignette post-fx is a tiny shader that darkens the edges of
    // the main camera; we scale its strength based on corrupted count.
    this.corruptionVignette = this.cameras.main.postFX.addVignette(0.5, 0.5, 0.98, 0.2);

    // Magenta glow edges overlay — sits ABOVE the grid but BELOW the HUD.
    // Used to paint the "glitch" edge color reaction when corruption spikes.
    this.corruptionEdgeGlow = this.add.graphics();
    this.corruptionEdgeGlow.setDepth(95).setAlpha(0);
    this.lastCorruptedCount = 0;

    // Draw cell backgrounds
    this.drawCellBackgrounds();

    // Try to load saved game
    const loadStatus = this.loadGame();
    const saved = loadStatus === 'loaded';
    const tutorialDone = localStorage.getItem('splintr_tutorial') === 'done';
    const gridIsEmpty = this.grid.getAll().length === 0;

    if (loadStatus === 'corrupt') {
      // Don't silently wipe progress (issue #25). Surface a recovery prompt
      // and halt the normal boot flow until the player acknowledges — the
      // prompt resets state and respawns on confirm.
      this.afterFirstPaint(() => this.showSaveRecoveryOverlay());
    } else if (!saved) {
      if (tutorialDone) {
        // Returning player with no save: full fresh-run flow.
        this.tutorialStep = 6;
        // Defer past the first painted frame so the player sees the HUD +
        // empty grid backdrop immediately, instead of staring at the same
        // black screen they were on while MenuScene was tearing down.
        this.afterFirstPaint(() => this.beginFreshRun());
      } else {
        // First-time player: guided tutorial. Tutorial uses Fire only, no draft needed.
        this.tutorialStep = 1;
        this.familyProgression.setActiveFamilies(this.familyProgression.getUnlockedFamilies());
        // Synchronously seed the step-1 fragments so the very first painted
        // frame already shows two Fire fragments on the grid. The full
        // welcome panel + dimming overlay then mounts on the next frame.
        this.preSpawnTutorialStep1();
        this.afterFirstPaint(() => this.startTutorial());
      }
    } else if (gridIsEmpty && tutorialDone) {
      // Saved progression exists but the grid is empty — this is the state
      // produced by startNewRun(). Show the draft (if needed) and respawn.
      this.tutorialStep = 6;
      this.afterFirstPaint(() => this.beginFreshRun());
    } else {
      this.tutorialStep = 6; // Tutorial already completed; resuming an in-progress run
    }

    // Create HUD
    this.createHUD();

    // Initial HUD update
    this.updateHUD();

    // Auto-save when user switches tabs or closes the browser. These paths
    // force an immediate write (flushSave) so a pending debounced save cannot
    // be lost to a background/kill.
    this.boundVisibilityHandler = () => {
      if (document.visibilityState === 'hidden') {
        this.flushSave();
      }
    };
    this.boundBeforeUnloadHandler = () => {
      this.flushSave();
    };
    document.addEventListener('visibilitychange', this.boundVisibilityHandler);
    window.addEventListener('beforeunload', this.boundBeforeUnloadHandler);

    // Clean up event listeners + DOM overlays + tweens + overlay Graphics when
    // the scene shuts down. Phaser destroys display list objects for us, but
    // leaves infinite tweens (splinter charge, tutorial glows, idle pulses)
    // running against dead targets, and our Maps keep holding stale refs —
    // both leak across scene restarts. Tear it all down explicitly.
    this.events.once('shutdown', () => {
      // Flush any pending debounced save before tearing the scene down — the
      // timer is owned by this.time and will be destroyed with the scene, so
      // an un-flushed trailing save would be lost.
      if (this.hasPendingSave) {
        this.flushSave();
      }
      if (this.pendingSaveTimer) {
        this.pendingSaveTimer.remove(false);
        this.pendingSaveTimer = null;
      }

      if (this.boundVisibilityHandler) {
        document.removeEventListener('visibilitychange', this.boundVisibilityHandler);
      }
      if (this.boundBeforeUnloadHandler) {
        window.removeEventListener('beforeunload', this.boundBeforeUnloadHandler);
      }
      this.draftOverlay?.close();
      this.draftOverlay = null;
      this.gameOverOverlay?.close();
      this.gameOverOverlay = null;
      this.pauseOverlay?.close();
      this.pauseOverlay = null;

      this.tweens.killAll();

      this.sprites.forEach((s) => s.destroy());
      this.sprites.clear();
      this.corruptionOverlays.forEach((g) => g.destroy());
      this.corruptionOverlays.clear();
      this.warningOverlays.forEach((g) => g.destroy());
      this.warningOverlays.clear();

      this.dragHighlight?.destroy();
      this.dragHighlight = null;
      this.splinterGlow?.destroy();
      this.splinterGlow = null;
      this.splinterTimer?.remove();
      this.splinterTimer = null;

      this.clearTutorialVisuals();

      this.discoveryToastQueue.length = 0;
      this.discoveryToastActive = false;

      this.unmountScanline();
    });

    // Expose test bridge for E2E tests
    (window as unknown as Record<string, unknown>).__SPLINTR__ = {
      getState: () => ({
        score: this.score,
        highScore: this.highScore,
        runMerges: this.runMerges,
        longestChain: this.longestChain,
        runsCompleted: this.runsCompleted,
        energy: this.energySystem.getEnergy(),
        maxEnergy: this.energySystem.getMaxEnergy(),
        overcharge: this.overchargeSystem.getCharge(),
        maxOvercharge: this.overchargeSystem.getMaxCharge(),
        codex: this.codexManager.getTotalStats(),
        wallet: this.shopManager.getWallet(),
        grid: this.grid.getAll().map((el) => ({ uid: el.uid, defId: el.defId, col: el.col, row: el.row, age: el.age, corrupted: el.corrupted })),
        tutorialStep: this.tutorialStep,
        generators: this.idleSystem.getGenerators().length,
        achievements: this.progressManager.getUnlockedAchievements().map((a) => a.id),
        stats: this.progressManager.getStats(),
        unlockedFamilies: this.familyProgression.getUnlockedFamilies(),
      }),
    };
  }

  /**
   * Write the current game state to localStorage right now. Only used at
   * critical boundaries (tab hide, menu nav, game over, scene shutdown);
   * gameplay paths should call `scheduleSave()` instead so cascades coalesce
   * into a single trailing-edge write (issue #26).
   */
  private saveGame(): void {
    const familyState = this.familyProgression.exportState();
    const gridState = this.grid.getAll().map((el) => ({
      uid: el.uid,
      defId: el.defId,
      col: el.col,
      row: el.row,
      age: el.age,
      corrupted: el.corrupted,
    }));
    const saveData = SaveManager.createSaveData(
      this.codexManager,
      this.shopManager,
      this.progressManager,
      {
        score: this.score,
        highScore: this.highScore,
        runsCompleted: this.runsCompleted,
        unlockedFamilies: familyState.unlockedFamilies,
        activeFamilies: familyState.activeFamilies,
        activeModifiers: this.activeModifiers.map((m) => m.id),
        modifierState: { splinterUsedThisRun: this.splinterUsedThisRun },
        gridState,
      }
    );

    const result = SaveManager.writeLocal(saveData);
    if (!result.ok && !this.saveQuotaWarned) {
      this.saveQuotaWarned = true;
      const message = result.reason === 'quota'
        ? 'Storage full — progress may not save'
        : 'Could not save — check browser storage';
      this.showSaveWarningToast(message);
    }
  }

  /**
   * Queue a save for the end of the current burst of activity. Coalesces any
   * number of calls within the debounce window into a single write — so a
   * cascade chain produces one localStorage write instead of one per step.
   * Callers that need durability guarantees (shutdown, tab hide) must call
   * `flushSave()` instead.
   */
  private scheduleSave(): void {
    this.hasPendingSave = true;
    if (this.pendingSaveTimer) return;
    this.pendingSaveTimer = this.time.delayedCall(
      GameScene.SAVE_DEBOUNCE_MS,
      () => {
        this.pendingSaveTimer = null;
        if (this.hasPendingSave) {
          this.hasPendingSave = false;
          this.saveGame();
        }
      }
    );
  }

  /**
   * Write immediately, cancelling any pending debounced save. Use at every
   * boundary where the process might be frozen or killed before the trailing
   * write would fire.
   */
  private flushSave(): void {
    if (this.pendingSaveTimer) {
      this.pendingSaveTimer.remove(false);
      this.pendingSaveTimer = null;
    }
    this.hasPendingSave = false;
    this.saveGame();
  }

  /**
   * Load game state from localStorage. Returns 'loaded' when a save was
   * restored, 'missing' when none exists, or 'corrupt' when the stored bytes
   * could not be parsed — the caller MUST show the player a recovery prompt
   * before overwriting the save (issue #25).
   */
  private loadGame(): 'loaded' | 'missing' | 'corrupt' {
    const result = SaveManager.readLocal();
    if (!result.ok) {
      if (result.reason === 'missing') return 'missing';
      return 'corrupt';
    }

    try {
      const gameState = SaveManager.loadSaveData(
        result.data,
        this.codexManager,
        this.shopManager,
        this.progressManager
      );

      // Restore score and run state
      this.score = gameState.score;
      this.highScore = gameState.highScore ?? 0;
      this.runsCompleted = gameState.runsCompleted ?? 0;
      this.activeModifiers = gameState.activeModifiers
        ? getModifiersByIds(gameState.activeModifiers)
        : [];
      this.splinterUsedThisRun = gameState.modifierState?.splinterUsedThisRun ?? false;
      this.applyModifierTunables();
      if (gameState.unlockedFamilies && gameState.unlockedFamilies.length > 0) {
        this.familyProgression.loadState({
          unlockedFamilies: gameState.unlockedFamilies,
          activeFamilies: gameState.activeFamilies,
        });
      }

      // Restore grid elements and create sprites
      for (const el of gameState.gridState) {
        const element: ElementInstance = {
          uid: el.uid,
          defId: el.defId,
          col: el.col,
          row: el.row,
          age: el.age ?? 0,
          corrupted: el.corrupted ?? false,
        };
        this.grid.set(el.col, el.row, element);
        this.createElementSprite(element);
        this.applyCorruptionVisualIfNeeded(element);
        this.uids.syncAbove(el.uid);
      }

      return 'loaded';
    } catch {
      return 'corrupt';
    }
  }

  update(time: number, _delta: number): void {
    // Update energy system (handles automatic refill)
    this.energySystem.update(time);

    // Idle system disabled — will be re-enabled once the mechanic is refined

    // Update HUD
    this.updateHUD();
  }

  /**
   * Spawn the opening 6 fragments for a fresh run, drawn from the active draft.
   */
  private spawnInitialFragments(): void {
    for (let i = 0; i < 6; i++) {
      const emptyCell = this.grid.findEmpty();
      if (emptyCell) {
        this.spawnElement(this.spawnSystem.pickSpawnDefId(), emptyCell.col, emptyCell.row);
      }
    }
  }

  /**
   * Begin a fresh run after the tutorial: either show the draft (if the
   * player has unlocked more families than the per-run cap) or jump straight
   * into spawning starter fragments.
   */
  private beginFreshRun(): void {
    if (this.familyProgression.needsDraft()) {
      this.showFamilyDraft();
    } else {
      this.familyProgression.setActiveFamilies(this.familyProgression.getUnlockedFamilies());
      this.rollRunModifiers();
      this.spawnInitialFragments();
      this.showModifierIntro();
    }
  }

  /**
   * Roll the modifier pool for a fresh run and reset any modifier state. Called
   * once per fresh run after the family draft (if any). Tutorial runs skip this
   * — modifiers would muddy the first teach.
   */
  private rollRunModifiers(): void {
    this.activeModifiers = pickRunModifiers();
    this.splinterUsedThisRun = false;
    this.applyModifierTunables();
    this.updateModifierBadges();
    this.scheduleSave();
  }

  /**
   * Push modifier tunables into the underlying systems whose state is tracked
   * outside of GameScene (decay threshold, etc). Called after roll + after load.
   * Keep this idempotent — it's also called on every save-restore.
   */
  private applyModifierTunables(): void {
    if (!this.decaySystem) return;
    this.decaySystem.setThresholdOffset(
      hasModifier(this.activeModifiers, ModifierId.Volatile) ? MODIFIER_VOLATILE_THRESHOLD_DELTA : 0
    );
  }

  /**
   * Show the pre-run draft overlay (review fix #2). Lets the player choose
   * up to MAX_ACTIVE_FAMILIES_PER_RUN families from their unlocked pool.
   *
   * v0.5 — rebuilt as a DOM-over-canvas card carousel (designer brief Part 1
   * item #2). Phaser now only handles the game board; the draft is a CSS
   * glassmorphism overlay with swipe/pointer drag + spring animations.
   */
  private showFamilyDraft(): void {
    const unlocked = this.familyProgression.getUnlockedFamilies();
    const preselected = this.familyProgression.getActiveFamilies().length > 0
      ? this.familyProgression.getActiveFamilies()
      : unlocked.filter(f => f !== Family.Wild).slice(0, MAX_ACTIVE_FAMILIES_PER_RUN);

    this.draftOverlay = new DraftOverlay({ unlocked, preselected });
    this.draftOverlay.on('confirm', (families) => {
      this.familyProgression.setActiveFamilies(families);
      this.spawnSystem.invalidateQueue();
      this.draftOverlay?.close();
      this.draftOverlay = null;
      this.rollRunModifiers();
      this.spawnInitialFragments();
      this.showModifierIntro();
      this.scheduleSave();
    });
    this.draftOverlay.open();
  }

  /**
   * Draw background rectangles for each grid cell.
   */
  private drawCellBackgrounds(): void {
    for (let col = 0; col < GRID_COLS; col++) {
      this.cellBackgrounds[col] = [];
      for (let row = 0; row < GRID_ROWS; row++) {
        const { x, y } = this.cellToPixel(col, row);
        const bg = this.add.image(
          x,
          y,
          'cell_bg'
        );
        bg.setDepth(0);
        this.cellBackgrounds[col][row] = bg;
      }
    }
  }

  /**
   * Game HUD — matches design/handoff/source/game.jsx.
   *
   * Layout (top → bottom):
   *   ┌──────────────────────────────────────────────────────┐
   *   │  [⏸]    [TIMER] [×N?]                        [? ]    │  top bar
   *   │                    SCORE 0                           │  score row
   *   │                OBJECTIVE · tagline                   │  objective strip
   *   │   [ ENERGY ][ OVERCHARGE ][ NEXT • • • ]            │  meters row
   *   │                                                      │
   *   │                    (grid)                            │
   *   │                                                      │
   *   │  [🔹 SHARDS]   [ ⚡ SPLINTER ]          [⚙]         │  bottom action bar
   *   └──────────────────────────────────────────────────────┘
   *
   * All glass pills go through drawGlassPill() so the WebGL renderer doesn't
   * re-triangulate the HUD every frame — that pattern is load-bearing for
   * perf (see commit 44cfc7c).
   */
  private createHUD(): void {
    const padX = 14;
    const topY = 14;
    const btnH = 40;

    // =============================================================
    // TOP BAR — pause / timer / combo / help
    // =============================================================
    // Pause button (left)
    const pauseSize = btnH;
    this.drawGlassPill(padX, topY, pauseSize, pauseSize, ACCENT_CYAN, 0.30);
    this.add
      .text(padX + pauseSize / 2, topY + pauseSize / 2, '⏸', {
        font: `600 18px ${FONT_DISPLAY}`,
        color: ACCENT_CYAN_CSS,
      })
      .setOrigin(0.5)
      .setDepth(102);
    const pauseZone = this.add
      .zone(padX + pauseSize / 2, topY + pauseSize / 2, Math.max(pauseSize, 44), Math.max(pauseSize, 44))
      .setInteractive({ useHandCursor: true })
      .setDepth(103);
    pauseZone.on('pointerup', () => {
      Haptics.selection();
      this.openPauseOverlay();
    });

    // Timer chip (center)
    const timerW = 96;
    const timerX = Math.round((GAME_WIDTH - timerW) / 2);
    this.drawGlassPill(timerX, topY, timerW, btnH);
    this.timerText = this.add
      .text(timerX + timerW / 2, topY + btnH / 2, '00:00', {
        font: `600 14px ${FONT_MONO}`,
        color: TEXT_PRIMARY_CSS,
      })
      .setOrigin(0.5)
      .setDepth(101);

    // Combo chip — to the right of the timer, hidden unless combo > 1
    const comboW = 48;
    const comboX = timerX + timerW + 8;
    this.comboChipBg = this.drawGlassPill(comboX, topY, comboW, btnH, 0xFFD600, 0.35);
    this.comboChipBg.setVisible(false);
    this.comboChipText = this.add
      .text(comboX + comboW / 2, topY + btnH / 2, '×2', {
        font: `700 14px ${FONT_DISPLAY}`,
        color: ACCENT_GOLD_CSS,
      })
      .setOrigin(0.5)
      .setDepth(101)
      .setVisible(false);

    // Help "?" button (right)
    const helpSize = btnH;
    const helpX = GAME_WIDTH - padX - helpSize;
    this.drawGlassPill(helpX, topY, helpSize, helpSize, ACCENT_CYAN, 0.30);
    this.add
      .text(helpX + helpSize / 2, topY + helpSize / 2, '?', {
        font: `700 20px ${FONT_DISPLAY}`,
        color: ACCENT_CYAN_CSS,
      })
      .setOrigin(0.5)
      .setDepth(102);
    const helpZone = this.add
      .zone(helpX + helpSize / 2, topY + helpSize / 2, Math.max(helpSize, 44), Math.max(helpSize, 44))
      .setInteractive({ useHandCursor: true })
      .setDepth(103);
    helpZone.on('pointerup', () => {
      Haptics.selection();
      this.scene.start('Help', { returnTo: 'Game' });
    });

    // =============================================================
    // SCORE ROW — standalone large text, centered
    // =============================================================
    const scoreY = topY + btnH + 14; // 68
    this.scoreText = this.add
      .text(GAME_WIDTH / 2, scoreY, '0', {
        font: `700 22px ${FONT_DISPLAY}`,
        color: TEXT_PRIMARY_CSS,
      })
      .setOrigin(0.5, 0)
      .setDepth(101);

    // =============================================================
    // OBJECTIVE STRIP — driven by active modifier(s) or static tagline
    // =============================================================
    const objY = scoreY + 28; // 96
    this.objectiveText = this.add
      .text(GAME_WIDTH / 2, objY, 'Merge. Splinter. Discover.', {
        font: `500 11px ${FONT_DISPLAY}`,
        color: TEXT_SECONDARY_CSS,
      })
      .setOrigin(0.5, 0)
      .setDepth(101);

    // =============================================================
    // METERS ROW — Energy | Overcharge | NEXT preview
    // Anchored to the base of the HUD so the grid stays at gridOffsetY=176.
    // =============================================================
    const meterY = this.gridOffsetY - 46; // 130
    const meterH = 38;
    const rowX = padX;
    const rowW = GAME_WIDTH - padX * 2;
    const cellGap = 6;
    const cellW = Math.floor((rowW - cellGap * 2) / 3);

    // --- Energy cell (left)
    const energyX = rowX;
    this.energyBarBg = this.drawBarGlass(null, energyX, meterY, cellW, meterH);
    this.energyBarFill = this.add.graphics().setDepth(101);
    this.energyBarLabel = this.add
      .text(energyX + 8, meterY + 6, 'ENERGY', {
        font: `600 9px ${FONT_DISPLAY}`,
        color: ACCENT_CYAN_CSS,
      })
      .setOrigin(0, 0)
      .setDepth(102);

    // --- Overcharge cell (center)
    const overX = rowX + cellW + cellGap;
    this.overchargeBarBg = this.drawBarGlass(null, overX, meterY, cellW, meterH);
    this.overchargeBarFill = this.add.graphics().setDepth(101);
    this.overchargeLabel = this.add
      .text(overX + 8, meterY + 6, 'OVERCHARGE', {
        font: `600 9px ${FONT_DISPLAY}`,
        color: ACCENT_CYAN_CSS,
      })
      .setOrigin(0, 0)
      .setDepth(102);

    // --- NEXT preview cell (right)
    const nextX = rowX + (cellW + cellGap) * 2;
    this.nextBarBg = this.drawBarGlass(null, nextX, meterY, cellW, meterH);
    this.nextLabel = this.add
      .text(nextX + 8, meterY + 6, 'NEXT', {
        font: `600 9px ${FONT_DISPLAY}`,
        color: ACCENT_CYAN_CSS,
      })
      .setOrigin(0, 0)
      .setDepth(102);
    // Icon slots sit along the bottom of the cell — populated in updateNextPreview.
    this.nextPreviewIcons = [];
    const iconSlots = 3;
    const iconSize = 18;
    const innerPad = 8;
    const iconStride = (cellW - innerPad * 2) / iconSlots;
    for (let i = 0; i < iconSlots; i++) {
      const icon = this.add
        .image(
          nextX + innerPad + iconStride * i + iconStride / 2,
          meterY + meterH - iconSize / 2 - 6,
          '__WHITE'
        )
        .setOrigin(0.5)
        .setDepth(102)
        .setDisplaySize(iconSize, iconSize)
        .setVisible(false);
      this.nextPreviewIcons.push(icon);
    }

    // Hint + corruption text — micro labels just below the meters row.
    this.hintText = this.add
      .text(GAME_WIDTH - padX, meterY + meterH + 4, '', {
        font: `500 10px ${FONT_DISPLAY}`,
        color: TEXT_TERTIARY_CSS,
      })
      .setOrigin(1, 0)
      .setDepth(100);
    this.corruptedText = this.add
      .text(padX, meterY + meterH + 4, '', {
        font: `700 11px ${FONT_DISPLAY}`,
        color: '#FF003C',
      })
      .setOrigin(0, 0)
      .setDepth(100);

    // --- Modifier badge row lives just under the hint row (smaller than before
    // because the objective strip now carries the primary modifier messaging).
    this.buildModifierBadges(rowX, meterY + meterH + 20, rowW);
    this.updateModifierBadges();

    // =============================================================
    // BOTTOM ACTION BAR — shards / SPLINTER / settings
    // =============================================================
    this.buildBottomActionBar();
  }

  /**
   * Bottom action bar: [Shards pill] [SPLINTER button] [Settings icon].
   * Positioned below the grid with safe-area breathing room.
   */
  private buildBottomActionBar(): void {
    const padX = 14;
    const barH = 56;
    const barY = GAME_HEIGHT - 90 - barH; // leaves ~90 px for iOS home indicator breathing room

    // Shards pill (left)
    const shardsW = 96;
    this.drawGlassPill(padX, barY + 8, shardsW, barH - 16);
    this.shardsText = this.add
      .text(padX + shardsW / 2, barY + barH / 2, '0', {
        font: `700 14px ${FONT_MONO}`,
        color: ACCENT_CYAN_CSS,
      })
      .setOrigin(0.5)
      .setDepth(101);

    // Settings icon (right)
    const gearSize = 48;
    const gearX = GAME_WIDTH - padX - gearSize;
    this.drawGlassPill(gearX, barY + 4, gearSize, gearSize, ACCENT_CYAN, 0.30);
    this.add
      .text(gearX + gearSize / 2, barY + 4 + gearSize / 2, '⚙', {
        font: `600 22px ${FONT_DISPLAY}`,
        color: ACCENT_CYAN_CSS,
      })
      .setOrigin(0.5)
      .setDepth(102);
    const gearZone = this.add
      .zone(gearX + gearSize / 2, barY + 4 + gearSize / 2, Math.max(gearSize, 44), Math.max(gearSize, 44))
      .setInteractive({ useHandCursor: true })
      .setDepth(103);
    gearZone.on('pointerup', () => {
      Haptics.selection();
      this.flushSave();
      this.scene.start('Settings');
    });

    // SPLINTER button (center) — 140×56, purple when Overcharge ready
    const splinterW = 144;
    const splinterX = Math.round((GAME_WIDTH - splinterW) / 2);
    this.splinterBtnGlow = null;
    this.splinterBtnBg = this.drawGlassPill(splinterX, barY, splinterW, barH, ACCENT_PURPLE, 0.35);
    this.splinterBtnLabel = this.add
      .text(splinterX + splinterW / 2, barY + barH / 2, 'SPLINTER', {
        font: `700 14px ${FONT_DISPLAY}`,
        color: TEXT_TERTIARY_CSS,
      })
      .setOrigin(0.5)
      .setDepth(102);
    this.splinterBtnZone = this.add
      .zone(splinterX + splinterW / 2, barY + barH / 2, splinterW, Math.max(barH, 44))
      .setInteractive({ useHandCursor: true })
      .setDepth(103);
    this.splinterBtnZone.on('pointerup', () => {
      if (!this.overchargeSystem.canSplinter()) {
        this.showInsufficientOverchargeToast();
        return;
      }
      const target = this.pickBestSplinterTarget();
      if (!target) {
        this.showInsufficientOverchargeToast();
        return;
      }
      Haptics.heavy();
      this.handleSplinterTap(target);
    });
  }

  /**
   * Pick a sensible target for a SPLINTER button tap — the highest-tier
   * on-board element eligible for splintering. Returns null if the board
   * has no valid targets.
   */
  private pickBestSplinterTarget(): ElementInstance | null {
    const candidates = this.grid.getAll().filter((el) => this.mergeEngine.canSplinter(el));
    if (candidates.length === 0) return null;
    candidates.sort((a, b) => {
      const tierA = getElementDefOrThrow(a.defId).tier;
      const tierB = getElementDefOrThrow(b.defId).tier;
      return tierB - tierA;
    });
    return candidates[0];
  }

  /**
   * Build (empty) glass pills for up to 2 modifier badges. The badges live
   * below the overcharge bar and are populated by updateModifierBadges() once
   * activeModifiers is known. Hidden entirely when no modifiers are active.
   */
  private buildModifierBadges(rowX: number, rowY: number, rowW: number): void {
    const badgeH = 18;
    const gap = 6;
    const slots = 2;
    const badgeW = Math.floor((rowW - gap * (slots - 1)) / slots);

    for (let i = 0; i < slots; i++) {
      const x = rowX + i * (badgeW + gap);
      const bg = this.drawGlassPill(x, rowY, badgeW, badgeH, 0xFFD600, 0.28);
      bg.setVisible(false).setDepth(100);
      const label = this.add
        .text(x + badgeW / 2, rowY + badgeH / 2, '', {
          font: '700 9px "Space Grotesk"',
          color: '#FFD600',
        })
        .setOrigin(0.5)
        .setDepth(101)
        .setVisible(false);
      this.modifierBadges.push({ bg, label });
    }
  }

  /**
   * Sync the modifier HUD badges with the currently active modifiers. Challenge
   * modifiers are tinted red; boons are tinted gold. Called after rollRunModifiers,
   * after save-load, and when modifiers are cleared.
   */
  private updateModifierBadges(): void {
    if (this.modifierBadges.length === 0) return;
    for (let i = 0; i < this.modifierBadges.length; i++) {
      const slot = this.modifierBadges[i];
      const mod = this.activeModifiers[i];
      if (!mod) {
        slot.bg.setVisible(false);
        slot.label.setVisible(false);
        continue;
      }
      const color = mod.tone === 'challenge' ? '#FF5E2A' : '#FFD600';
      slot.label.setText(mod.name.toUpperCase());
      slot.label.setColor(color);
      slot.bg.setVisible(true);
      slot.label.setVisible(true);
    }
  }

  /**
   * Slide-in banner announcing this run's modifiers. Auto-dismisses. Skipped
   * when no modifiers are active (e.g., on a resumed save from an old build).
   */
  private showModifierIntro(): void {
    if (this.activeModifiers.length === 0) return;

    const cx = GAME_WIDTH / 2;
    const cy = this.gridOffsetY + 40;
    const lines = this.activeModifiers.map((m) => `${m.name.toUpperCase()} — ${m.description}`);

    const panel = this.add.graphics().setDepth(220).setAlpha(0);
    const panelW = GAME_WIDTH - 40;
    const panelH = 28 + lines.length * 38;
    panel.fillStyle(0x0A0B11, 0.94);
    panel.fillRoundedRect(20, cy - panelH / 2, panelW, panelH, 12);
    panel.lineStyle(1, 0xFFD600, 0.55);
    panel.strokeRoundedRect(20, cy - panelH / 2, panelW, panelH, 12);

    const title = this.add
      .text(cx, cy - panelH / 2 + 14, 'THIS RUN', {
        font: '700 10px "Space Grotesk"',
        color: '#FFD600',
      })
      .setOrigin(0.5, 0)
      .setDepth(221)
      .setAlpha(0);

    const bodies: Phaser.GameObjects.Text[] = [];
    for (let i = 0; i < this.activeModifiers.length; i++) {
      const mod = this.activeModifiers[i];
      const lineY = cy - panelH / 2 + 32 + i * 38;
      const nameColor = mod.tone === 'challenge' ? '#FF5E2A' : '#FFD600';
      const name = this.add
        .text(cx, lineY, mod.name, {
          font: '700 13px "Space Grotesk"',
          color: nameColor,
        })
        .setOrigin(0.5, 0)
        .setDepth(221)
        .setAlpha(0);
      const desc = this.add
        .text(cx, lineY + 16, mod.description, {
          font: '500 10px Inter',
          color: '#8892B0',
          align: 'center',
          wordWrap: { width: panelW - 24 },
        })
        .setOrigin(0.5, 0)
        .setDepth(221)
        .setAlpha(0);
      bodies.push(name, desc);
    }

    const targets = [panel, title, ...bodies];
    this.tweens.add({
      targets,
      alpha: 1,
      duration: 220,
      ease: Phaser.Math.Easing.Quadratic.Out,
      hold: 2600,
      yoyo: true,
      onComplete: () => {
        for (const t of targets) t.destroy();
      },
    });

    Haptics.medium();
  }

  /**
   * Draw a single "frosted glass" pill and bake it into a texture-backed
   * Image so it doesn't get re-triangulated every frame.
   *
   * **Why this isn't a Graphics:** Phaser's WebGL renderer triangulates
   * Graphics paths *on every render*, even when the path hasn't changed.
   * `fillRoundedRect` walks earcut for every redraw, which a CDP CPU
   * profile measured at ~2400ms across a 2-second window for the static
   * HUD. Generating the texture once and rendering an Image instead is
   * O(1) per frame and shows up as zero earcut time in the profile.
   */
  private drawGlassPill(
    x: number,
    y: number,
    w: number,
    h: number,
    strokeColor: number = 0x00F0FF,
    strokeAlpha: number = 0.22
  ): Phaser.GameObjects.Image {
    // Generate a texture key unique to (size, color, alpha) so identical
    // pills share one underlying texture.
    const key = `pill_${w}x${h}_${strokeColor.toString(16)}_${Math.round(strokeAlpha * 100)}`;
    if (!this.textures.exists(key)) {
      const tmp = this.make.graphics({ x: 0, y: 0 }, false);
      tmp.fillStyle(0x0A0B11, 0.78);
      tmp.fillRoundedRect(0, 0, w, h, 999);
      tmp.lineStyle(1, strokeColor, strokeAlpha);
      tmp.strokeRoundedRect(0.5, 0.5, w - 1, h - 1, 999);
      tmp.generateTexture(key, w, h);
      tmp.destroy();
    }
    const img = this.add.image(x, y, key).setOrigin(0, 0).setDepth(100);
    return img;
  }

  /**
   * Draw the rounded-pill frame for the overcharge bar — same texture-baked
   * approach as drawGlassPill. We replace the existing Graphics in place by
   * destroying it and pointing the field at a new Image.
   */
  private drawBarGlass(
    target: Phaser.GameObjects.Graphics | null,
    x: number,
    y: number,
    w: number,
    h: number
  ): Phaser.GameObjects.Image {
    if (target) target.destroy();
    const key = `bar_glass_${w}x${h}`;
    if (!this.textures.exists(key)) {
      const tmp = this.make.graphics({ x: 0, y: 0 }, false);
      tmp.fillStyle(0x0A0B11, 0.78);
      tmp.fillRoundedRect(0, 0, w, h, h / 2);
      tmp.lineStyle(1, 0x00F0FF, 0.25);
      tmp.strokeRoundedRect(0.5, 0.5, w - 1, h - 1, h / 2);
      tmp.generateTexture(key, w, h);
      tmp.destroy();
    }
    return this.add.image(x, y, key).setOrigin(0, 0).setDepth(100);
  }

  /**
   * Redraw the overcharge fill bar based on current charge fraction.
   * Color shifts from cyan → premium purple as it crosses the splinter threshold.
   *
   * Also fires an explosive "ready" reaction on the 0→1 transition:
   *   - cyan particle burst from the bar
   *   - brief camera flash + bar scale pulse
   *   - heavy haptic (Taptic engine on iOS, vibration API on Android/web)
   * (Designer brief §5.)
   */
  private redrawOverchargeBar(): void {
    if (!this.overchargeBarFill || !this.overchargeBarBg) return;
    const bg = this.overchargeBarBg;
    const barX = Math.round(bg.x);
    const barY = Math.round(bg.y);
    const barW = Math.round(bg.displayWidth);
    const barH = Math.round(bg.displayHeight);
    const fillMax = barW - 16;
    const fillHeight = 6;
    const fraction = this.overchargeSystem.getFraction();
    const fillW = Math.max(0, Math.floor(fillMax * fraction));
    const ready = this.overchargeSystem.canSplinter();

    const geometryChanged = fillW !== this.lastOverchargeFillW || ready !== this.lastOverchargeReady;
    if (geometryChanged) {
      this.overchargeBarFill.clear();
      this.lastOverchargeFillW = fillW;
      this.lastOverchargeReady = ready;
    }
    if (geometryChanged && fillW > 0) {
      const fillColor = ready ? ACCENT_PURPLE : ACCENT_CYAN;
      this.overchargeBarFill.fillStyle(fillColor, ready ? 0.95 : 0.82);
      this.overchargeBarFill.fillRoundedRect(barX + 8, barY + barH - 12, fillW, fillHeight, fillHeight / 2);
      // Top highlight stripe for a subtle "glass" feel
      this.overchargeBarFill.fillStyle(0xF5F7FA, ready ? 0.3 : 0.18);
      this.overchargeBarFill.fillRect(barX + 9, barY + barH - 11, fillW - 2, 1);
    }

    if (ready && !this.wasOverchargeReady) {
      this.triggerOverchargeReadyBurst(barX, barY, barW, barH);
    }
    this.wasOverchargeReady = ready;
  }

  /**
   * Cyan particle burst + camera flash + heavy haptic when Overcharge first
   * crosses the splinter threshold mid-run. The player now has power — give
   * it explosive feedback.
   */
  private triggerOverchargeReadyBurst(
    barX: number,
    barY: number,
    barW: number,
    barH: number
  ): void {
    Haptics.heavy();

    // 10-particle ring burst from the bar center, colored cyan with a little variance.
    const cx = barX + barW / 2;
    const cy = barY + barH / 2;
    for (let i = 0; i < 10; i++) {
      const angle = (i / 10) * Math.PI * 2;
      const radius = 44 + Math.random() * 16;
      const p = this.add.circle(cx, cy, 3, 0x00F0FF, 0.95);
      p.setDepth(150);
      this.tweens.add({
        targets: p,
        x: cx + Math.cos(angle) * radius,
        y: cy + Math.sin(angle) * radius,
        alpha: 0,
        scaleX: 0.2,
        scaleY: 0.2,
        duration: 620,
        ease: Phaser.Math.Easing.Quadratic.Out,
        onComplete: () => p.destroy(),
      });
    }

    // Bar scale pulse
    if (this.overchargeBarBg && this.overchargeBarFill) {
      this.tweens.add({
        targets: [this.overchargeBarBg, this.overchargeBarFill],
        scaleY: { from: 1.8, to: 1 },
        duration: 380,
        ease: Phaser.Math.Easing.Elastic.Out,
      });
    }

    // Subtle cyan camera flash — kept very short + low alpha so the bar is
    // the hero, not the whole screen.
    this.cameras.main.flash(140, 0, 240, 255, false);
  }

  /**
   * Update all HUD text displays. Thin orchestrator — each private method
   * below is individually diff-gated so per-frame work stays bounded.
   */
  private updateHUD(): void {
    this.updateScore();
    this.updateTimer();
    this.updateCombo();
    this.updateObjective();
    this.updateMetersEnergy();
    this.updateNextPreview();
    this.updateShards();
    this.updateSplinterButton();

    // Next-discovery hint — getNextDiscoveryHint walks the whole grid, so
    // we still pay that cost, but at least the texture upload is gated.
    const hintStr = this.getNextDiscoveryHint();
    if (hintStr !== this.lastHintText) {
      this.hintText.setText(hintStr);
      this.lastHintText = hintStr;
    }

    // Single grid scan for the corrupted-count, reused below.
    const corruptedCount = this.countCorrupted();

    // Corrupted blocker count — only show when > 0
    if (corruptedCount > 0) {
      const corruptedStr = `\u26A0 ${corruptedCount} BLOCKED`;
      if (corruptedStr !== this.lastCorruptedText) {
        this.corruptedText.setText(corruptedStr);
        this.lastCorruptedText = corruptedStr;
      }
      if (!this.lastCorruptedVisible) {
        this.corruptedText.setVisible(true);
        this.lastCorruptedVisible = true;
      }
    } else if (this.lastCorruptedVisible) {
      this.corruptedText.setVisible(false);
      this.lastCorruptedVisible = false;
    }

    // Overcharge bar
    this.redrawOverchargeBar();

    // Corruption vignette + glow edges
    this.updateCorruptionFX(corruptedCount);
  }

  private updateScore(): void {
    const scoreStr = this.score.toLocaleString();
    if (scoreStr !== this.lastScoreText) {
      this.scoreText.setText(scoreStr);
      this.lastScoreText = scoreStr;
    }
  }

  private updateTimer(): void {
    const now = performance.now();
    const paused = this.pauseStartedAt !== null
      ? this.pausedElapsedMs + (now - this.pauseStartedAt)
      : this.pausedElapsedMs;
    const elapsedMs = Math.max(0, now - this.runStartMs - paused);
    const totalSec = Math.floor(elapsedMs / 1000);
    const mm = Math.floor(totalSec / 60).toString().padStart(2, '0');
    const ss = (totalSec % 60).toString().padStart(2, '0');
    const timerStr = `${mm}:${ss}`;
    if (timerStr !== this.lastTimerText) {
      this.timerText.setText(timerStr);
      this.lastTimerText = timerStr;
    }
  }

  private updateCombo(): void {
    const step = this.activeChainStep;
    if (step === this.lastComboStep) return;
    this.lastComboStep = step;
    const visible = step > 1;
    this.comboChipBg.setVisible(visible);
    this.comboChipText.setVisible(visible);
    if (visible) {
      this.comboChipText.setText(`×${step}`);
    }
  }

  private updateObjective(): void {
    const primary = this.activeModifiers[0];
    const text = primary
      ? `${primary.name.toUpperCase()} · ${primary.description}`
      : 'Merge. Splinter. Discover.';
    if (text === this.lastObjectiveText) return;
    this.lastObjectiveText = text;
    const maxChars = 56;
    this.objectiveText.setText(
      text.length > maxChars ? text.slice(0, maxChars - 1) + '…' : text
    );
  }

  private updateMetersEnergy(): void {
    if (!this.energyBarFill || !this.energyBarBg) return;
    const bg = this.energyBarBg;
    const cellW = Math.round(bg.displayWidth);
    const cellH = Math.round(bg.displayHeight);
    const x = Math.round(bg.x);
    const y = Math.round(bg.y);
    const fillMax = cellW - 16;
    const frac = Math.min(1, Math.max(0, this.energySystem.getEnergy() / this.energySystem.getMaxEnergy()));
    const fillW = Math.floor(fillMax * frac);
    if (fillW === this.lastEnergyFillW) return;
    this.lastEnergyFillW = fillW;
    this.energyBarFill.clear();
    if (fillW > 0) {
      this.energyBarFill.fillStyle(ACCENT_CYAN, 0.82);
      this.energyBarFill.fillRoundedRect(x + 8, y + cellH - 12, fillW, 6, 3);
    }
  }

  private updateNextPreview(): void {
    const next = this.spawnSystem.peekNext(this.nextPreviewIcons.length);
    const key = next.join('|');
    if (key === this.lastNextPreviewKey) return;
    this.lastNextPreviewKey = key;
    for (let i = 0; i < this.nextPreviewIcons.length; i++) {
      const icon = this.nextPreviewIcons[i];
      const defId = next[i];
      if (!defId || !this.textures.exists(defId)) {
        icon.setVisible(false);
        continue;
      }
      icon.setTexture(defId);
      icon.setDisplaySize(18, 18);
      icon.setVisible(true);
    }
  }

  private updateShards(): void {
    const count = this.shopManager.getShards();
    const str = count.toLocaleString();
    if (str === this.lastShardsText) return;
    this.lastShardsText = str;
    this.shardsText.setText(str);
  }

  private updateSplinterButton(): void {
    const ready = this.overchargeSystem.canSplinter();
    if (ready === this.lastSplinterReady) return;
    this.lastSplinterReady = ready;
    this.splinterBtnLabel.setColor(ready ? TEXT_PRIMARY_CSS : TEXT_TERTIARY_CSS);
    this.tweens.killTweensOf(this.splinterBtnBg);
    this.splinterBtnBg.setScale(1);
    if (ready) {
      this.tweens.add({
        targets: this.splinterBtnBg,
        scaleX: { from: 1, to: 1.03 },
        scaleY: { from: 1, to: 1.03 },
        duration: 700,
        yoyo: true,
        repeat: -1,
        ease: Phaser.Math.Easing.Sine.InOut,
      });
    }
  }

  /**
   * Count corrupted elements on the grid. Single scan, no allocation.
   * Used by updateHUD + updateCorruptionFX so we don't walk the grid twice
   * per frame.
   */
  private countCorrupted(): number {
    const all = this.grid.getAll();
    let count = 0;
    for (let i = 0; i < all.length; i++) {
      if (all[i].corrupted) count++;
    }
    return count;
  }

  /**
   * Sync the corruption post-FX (vignette + edge glow) to the current board
   * state. Called every frame from updateHUD; cheap because the vignette
   * only mutates a few uniform floats.
   *
   * Design intent (review §4): the player should *feel* the corruption
   * tightening around them. As the corrupted count rises from 0 → 6+, the
   * vignette closes in (radius shrinks, strength rises) and a magenta edge
   * glow fades in. On a spike (new corruption this frame), we pulse the
   * glow + shake the camera + fire an error haptic.
   */
  private updateCorruptionFX(corruptedCount: number): void {
    if (!this.corruptionVignette || !this.corruptionEdgeGlow) return;

    // Spike reaction — corruption increased since last frame. Always
    // checked so haptics + shake fire on the frame the count goes up.
    if (corruptedCount > this.lastCorruptedCount) {
      Haptics.error();
      this.cameras.main.shake(220, 0.004 * (corruptedCount - this.lastCorruptedCount));
      this.tweens.add({
        targets: this.corruptionEdgeGlow,
        alpha: { from: 1.6, to: 1 },
        duration: 320,
        ease: Phaser.Math.Easing.Quadratic.Out,
      });
    }
    this.lastCorruptedCount = corruptedCount;

    // Skip the redraw work if the corrupted count hasn't changed since the
    // last frame. The vignette uniforms and the magenta edge ring are both
    // pure functions of the count, so there's nothing to update.
    if (corruptedCount === this.lastFXCorruptedCount) return;
    this.lastFXCorruptedCount = corruptedCount;

    // Normalise to 0..1 over 0..6 corrupted blockers.
    const intensity = Math.min(corruptedCount / 6, 1);

    // Vignette radius: 0.98 (no darkening) → 0.55 (strong tunnel). Strength
    // ramps from 0.2 → 0.85 in parallel.
    this.corruptionVignette.radius = 0.98 - intensity * 0.43;
    this.corruptionVignette.strength = 0.2 + intensity * 0.65;

    // Redraw the magenta edge ring. Its alpha scales with intensity; when
    // there's no corruption, the graphics object is fully transparent.
    this.corruptionEdgeGlow.clear();
    if (intensity > 0) {
      const alpha = 0.25 + intensity * 0.55;
      const pad1 = 2;
      const pad2 = 10;
      const pad3 = 22;
      this.corruptionEdgeGlow.lineStyle(2, 0xFF003C, alpha);
      this.corruptionEdgeGlow.strokeRect(pad1, pad1, GAME_WIDTH - pad1 * 2, GAME_HEIGHT - pad1 * 2);
      this.corruptionEdgeGlow.lineStyle(6, 0xFF003C, alpha * 0.5);
      this.corruptionEdgeGlow.strokeRect(pad2, pad2, GAME_WIDTH - pad2 * 2, GAME_HEIGHT - pad2 * 2);
      this.corruptionEdgeGlow.lineStyle(14, 0x8A00FF, alpha * 0.18);
      this.corruptionEdgeGlow.strokeRect(pad3, pad3, GAME_WIDTH - pad3 * 2, GAME_HEIGHT - pad3 * 2);
      this.corruptionEdgeGlow.setAlpha(1);
    } else {
      this.corruptionEdgeGlow.setAlpha(0);
    }
  }

  /**
   * Find the lowest-tier undiscovered element the player could create
   * from elements currently on the grid.
   */
  private getNextDiscoveryHint(): string {
    const gridElements = this.grid.getAll();
    // Count occurrences of each defId
    const counts = new Map<string, number>();
    for (const el of gridElements) {
      counts.set(el.defId, (counts.get(el.defId) ?? 0) + 1);
    }

    // Check which merge results are achievable and undiscovered
    for (const [defId, count] of counts) {
      if (count < 2) continue;
      const resultId = getMergeResult(defId);
      if (!resultId) continue;
      if (!this.codexManager.isDiscovered(resultId)) {
        const sourceDef = getElementDefOrThrow(defId);
        return `Merge 2 ${sourceDef.name} \u2192 ???`;
      }
    }

    return '';
  }

  /**
   * Convert grid coordinates to pixel coordinates (center of cell).
   */
  private cellToPixel(col: number, row: number): { x: number; y: number } {
    return {
      x: this.gridOffsetX + col * (CELL_SIZE + CELL_GAP) + CELL_SIZE / 2,
      y: this.gridOffsetY + row * (CELL_SIZE + CELL_GAP) + CELL_SIZE / 2,
    };
  }

  /**
   * Convert pixel coordinates to grid coordinates with bounds clamping.
   */
  private pixelToCell(px: number, py: number): { col: number; row: number } {
    const col = Math.floor(
      (px - this.gridOffsetX) / (CELL_SIZE + CELL_GAP)
    );
    const row = Math.floor(
      (py - this.gridOffsetY) / (CELL_SIZE + CELL_GAP)
    );

    return {
      col: Phaser.Math.Clamp(col, 0, GRID_COLS - 1),
      row: Phaser.Math.Clamp(row, 0, GRID_ROWS - 1),
    };
  }

  /**
   * Create a sprite for an element instance with drag interactions.
   */
  /**
   * Idle-float is suppressed for reduced-motion users and during SSR/tests
   * where `window.matchMedia` is unavailable.
   */
  private idleFloatDisabled(): boolean {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return true;
    try {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch {
      return true;
    }
  }

  /**
   * Mount a yoyo'd idle bob on a tile sprite. Pure decoration — the y delta
   * (±1.5 px) is deliberately small so the bob reads as parallax shimmer, not
   * drift. We own the tween in `idleTweens` so drag/merge/cascade code can
   * pause it before mutating sprite.y and resume it after settle.
   */
  private attachIdleFloat(sprite: Phaser.GameObjects.Sprite, element: ElementInstance): void {
    if (this.idleFloatDisabled()) return;
    // Tutorial steps 1–5 keep exact tile geometry so glow highlights line up.
    if (this.tutorialStep > 0 && this.tutorialStep < 6) return;
    // Replace any existing entry defensively.
    const existing = this.idleTweens.get(element.uid);
    if (existing) {
      existing.stop();
      existing.remove();
      this.idleTweens.delete(element.uid);
    }
    const baselineY = sprite.y;
    const delay = computeIdleStagger(element.row, element.col, GRID_COLS);
    const tween = this.tweens.add({
      targets: sprite,
      y: baselineY + 1.5,
      duration: 1600,
      ease: Phaser.Math.Easing.Sine.InOut,
      yoyo: true,
      repeat: -1,
      delay,
    });
    this.idleTweens.set(element.uid, tween);
  }

  /**
   * Tear down the idle-float tween for a uid before any positional mutation
   * (drag start, merge travel, cascade slide). Safe to call when no tween is
   * registered. Does NOT snap sprite y — the caller owns the final position.
   */
  private pauseIdleFloat(uid: number): void {
    const tween = this.idleTweens.get(uid);
    if (!tween) return;
    tween.stop();
    tween.remove();
    this.idleTweens.delete(uid);
  }

  /**
   * Re-snap a sprite to its canonical cell y, then re-attach a fresh idle
   * tween from that baseline. Call from the `onComplete` of any positional
   * tween that left the sprite at rest (snap-back, merge settle, cascade
   * settle). Using cellToPixel guarantees the new baseline is pixel-exact.
   */
  private resumeIdleFloat(uid: number, sprite: Phaser.GameObjects.Sprite, element: ElementInstance): void {
    const { y } = this.cellToPixel(element.col, element.row);
    sprite.y = y;
    this.attachIdleFloat(sprite, element);
  }

  /**
   * Attach idle-float to every live sprite. Called once the tutorial exits
   * (step 6) so the whole board begins bobbing together.
   */
  private mountIdleFloatAll(): void {
    if (this.idleFloatDisabled()) return;
    for (const [uid, sprite] of this.sprites) {
      const element = this.grid.getAll().find(e => e.uid === uid);
      if (element) this.attachIdleFloat(sprite, element);
    }
  }

  private createElementSprite(element: ElementInstance): Phaser.GameObjects.Sprite {
    const { x, y } = this.cellToPixel(element.col, element.row);
    const sprite = this.add.sprite(x, y, element.defId);
    sprite.setInteractive();
    sprite.setDepth(10);

    this.sprites.set(element.uid, sprite);
    this.attachIdleFloat(sprite, element);

    // Drag events
    this.input.setDraggable(sprite);

    sprite.on('dragstart', (pointer: Phaser.Input.Pointer) => {
      this.cancelSplinterCharge();
      this.handleDragStart(sprite, element, pointer);
    });

    sprite.on('drag', (pointer: Phaser.Input.Pointer) => {
      this.handleDrag(sprite, pointer);
    });

    sprite.on('dragend', (pointer: Phaser.Input.Pointer) => {
      this.handleDragEnd(sprite, element, pointer);
    });

    // Splinter via long-press (500ms hold) — disabled during merge tutorial steps
    sprite.on('pointerdown', () => {
      this.cancelSplinterCharge();
      if (this.isAnimating) return; // Block during chain/spawn animations
      // Tutorial guardrail: splinter is fully blocked during steps 1–4
      // (merges + cascade demo). In step 5 only the tutorial's spotlit
      // crystal can be splintered.
      if (this.tutorialStep > 0 && this.tutorialStep < 5) return;
      if (this.tutorialStep === 5 && this.tutorialAllowedSplinterUid !== element.uid) return;
      if (!this.mergeEngine.canSplinter(element)) return;

      this.splinterTarget = element;

      // Show charging glow
      const { x, y } = this.cellToPixel(element.col, element.row);
      this.splinterGlow = this.add.graphics();
      this.splinterGlow.setDepth(this.tutorialStep > 0 && this.tutorialStep < 6 ? 156 : 15);
      this.splinterGlow.lineStyle(3, FAMILY_COLORS[getElementDefOrThrow(element.defId).family], 0.6);
      this.splinterGlow.strokeCircle(x, y, CELL_SIZE / 2 + 4);
      this.splinterGlow.setAlpha(0);
      this.tweens.add({
        targets: this.splinterGlow,
        alpha: 1,
        duration: 750,
        ease: Phaser.Math.Easing.Linear,
      });

      // Fire splinter after 750ms hold — only if not dragging
      this.splinterTimer = this.time.delayedCall(750, () => {
        if (this.isDragging) {
          this.cancelSplinterCharge();
          return;
        }
        this.cancelSplinterGlow();
        this.handleSplinterTap(element);
        this.splinterTarget = null;
      });
    });

    sprite.on('pointerup', () => {
      this.cancelSplinterCharge();
    });

    return sprite;
  }

  /**
   * Remove a sprite from the scene.
   */
  private removeElementSprite(uid: number): void {
    this.pauseIdleFloat(uid);
    const sprite = this.sprites.get(uid);
    if (sprite) {
      this.tweens.killTweensOf(sprite);
      sprite.destroy();
      this.sprites.delete(uid);
    }
    // Clean up any overlays
    this.removeCorruptionOverlay(uid);
    this.removeWarningOverlay(uid);
  }

  /**
   * Spawn an element at a grid position with pop-in animation.
   */
  private spawnElement(
    defId: string,
    col: number,
    row: number
  ): ElementInstance {
    const element: ElementInstance = {
      uid: this.uids.mint(),
      defId,
      col,
      row,
      age: 0,
      corrupted: false,
    };

    this.grid.set(col, row, element);
    this.createElementSprite(element);

    return element;
  }

  /**
   * Handle drag start.
   */
  private handleDragStart(
    sprite: Phaser.GameObjects.Sprite,
    element: ElementInstance,
    pointer: Phaser.Input.Pointer
  ): void {
    // Block input during chain/spawn animations
    if (this.isAnimating) return;

    // Tutorial guardrail: during an active merge/cascade step, only the
    // highlighted elements are draggable. Rejected drags are marked so the
    // subsequent drag/dragend handlers no-op (the sprite stays put) — the
    // player can't inadvertently rearrange the board off-script.
    if (this.isTutorialGuardActive() && !this.tutorialAllowedSourceUids.has(element.uid)) {
      (sprite as Phaser.GameObjects.Sprite & { __tutorialBlocked?: boolean }).__tutorialBlocked = true;
      return;
    }
    (sprite as Phaser.GameObjects.Sprite & { __tutorialBlocked?: boolean }).__tutorialBlocked = false;

    // Stop the yoyo'd idle bob so pointer.y drives the sprite alone; otherwise
    // the tween still fights for the y channel and the tile jitters under the
    // finger.
    this.pauseIdleFloat(element.uid);

    this.isDragging = true;
    this.selectedSprite = sprite;
    this.dragStartCol = element.col;
    this.dragStartRow = element.row;

    sprite.setDepth(this.tutorialStep > 0 && this.tutorialStep < 6 ? 195 : 50);
    sprite.setAlpha(0.9);
    sprite.setScale(1.15);

    // Show selection ring behind the dragged element
    const def = getElementDefOrThrow(element.defId);
    const familyColor = FAMILY_COLORS[def.family];
    this.dragHighlight = this.add.graphics();
    this.dragHighlight.setDepth(this.tutorialStep > 0 && this.tutorialStep < 6 ? 194 : 49);
    this.dragHighlight.lineStyle(3, familyColor, 0.7);
    this.dragHighlight.strokeCircle(0, 0, CELL_SIZE / 2 + 6);
    this.dragHighlight.fillStyle(familyColor, 0.12);
    this.dragHighlight.fillCircle(0, 0, CELL_SIZE / 2 + 6);
    this.dragHighlight.setPosition(sprite.x, sprite.y);

    // Highlight valid merge targets (matching elements)
    const matchingElements = this.grid.getAll().filter(
      el => el.defId === element.defId && el.uid !== element.uid
    );
    for (const match of matchingElements) {
      const cellBg = this.cellBackgrounds[match.col]?.[match.row];
      if (cellBg) {
        cellBg.setTint(familyColor);
        cellBg.setAlpha(0.5);
      }
    }
  }

  /**
   * Handle drag movement.
   */
  private handleDrag(
    sprite: Phaser.GameObjects.Sprite,
    pointer: Phaser.Input.Pointer
  ): void {
    // Tutorial-blocked drags must not move the sprite off its cell.
    if ((sprite as Phaser.GameObjects.Sprite & { __tutorialBlocked?: boolean }).__tutorialBlocked) return;

    sprite.x = pointer.x;
    sprite.y = pointer.y;

    // Move selection ring with sprite
    if (this.dragHighlight) {
      this.dragHighlight.setPosition(pointer.x, pointer.y);
    }
  }

  /**
   * Clear drag selection visuals (highlight ring + merge target tints).
   */
  private clearDragFeedback(): void {
    if (this.dragHighlight) {
      this.dragHighlight.destroy();
      this.dragHighlight = null;
    }
    // Clear all cell tints
    for (let col = 0; col < GRID_COLS; col++) {
      for (let row = 0; row < GRID_ROWS; row++) {
        const cellBg = this.cellBackgrounds[col]?.[row];
        if (cellBg) {
          cellBg.clearTint();
          cellBg.setAlpha(1);
        }
      }
    }
  }

  /**
   * Handle drag end - merge or move.
   */
  private handleDragEnd(
    sprite: Phaser.GameObjects.Sprite,
    element: ElementInstance,
    pointer: Phaser.Input.Pointer
  ): void {
    // Tutorial-blocked drags never started — do nothing (sprite stayed put).
    const flag = sprite as Phaser.GameObjects.Sprite & { __tutorialBlocked?: boolean };
    if (flag.__tutorialBlocked) {
      flag.__tutorialBlocked = false;
      return;
    }

    this.isDragging = false;
    this.clearDragFeedback();

    const { col: targetCol, row: targetRow } = this.pixelToCell(
      pointer.x,
      pointer.y
    );

    // Check if target is valid and different from source
    if (targetCol === this.dragStartCol && targetRow === this.dragStartRow) {
      // No movement, snap back
      this.snapSpriteBack(sprite, element);
      return;
    }

    const targetElement = this.grid.get(targetCol, targetRow);

    // Tutorial guardrail: reject drops on non-allowed targets (including
    // dropping into an empty cell, which would otherwise just move the
    // element and let the player break the scripted board layout).
    if (this.isTutorialGuardActive()) {
      if (!targetElement || !this.tutorialAllowedTargetUids.has(targetElement.uid)) {
        this.snapSpriteBack(sprite, element);
        return;
      }
    }

    if (targetElement && this.mergeEngine.canMerge(element, targetElement)) {
      // Merge! (Energy no longer gates merges — sessions bounded by game over)
      this.performMerge(
        sprite,
        element,
        targetCol,
        targetRow
      );
    } else {
      // Invalid target, snap back
      this.snapSpriteBack(sprite, element);
    }
  }

  /**
   * Snap a sprite back to its original position.
   */
  private snapSpriteBack(
    sprite: Phaser.GameObjects.Sprite,
    element: ElementInstance
  ): void {
    const { x, y } = this.cellToPixel(element.col, element.row);
    // Spring (Back.Out) instead of Quadratic — the element gives a satisfying
    // little overshoot when a drag is rejected, matching the DOM layer's
    // cubic-bezier spring curves (designer brief §2).
    this.tweens.add({
      targets: sprite,
      x,
      y,
      scaleX: 1,
      scaleY: 1,
      alpha: 1,
      duration: DROP_TWEEN_DURATION * 1.4,
      ease: Phaser.Math.Easing.Back.Out,
      onComplete: () => {
        sprite.setDepth(this.tutorialStep > 0 && this.tutorialStep < 6 ? 155 : 10);
        this.resumeIdleFloat(element.uid, sprite, element);
      },
    });
  }

  /**
   * Perform a merge operation with animations.
   */
  private performMerge(
    sourceSprite: Phaser.GameObjects.Sprite,
    sourceElement: ElementInstance,
    targetCol: number,
    targetRow: number
  ): void {
    // Capture the target element info before the merge removes it from the grid
    const targetElement = this.grid.get(targetCol, targetRow);
    const targetUid = targetElement?.uid;

    const mergeResult = this.mergeEngine.merge(
      sourceElement.col,
      sourceElement.row,
      targetCol,
      targetRow
    );

    if (!mergeResult) {
      this.snapSpriteBack(sourceSprite, sourceElement);
      return;
    }

    // Track discovery in CodexManager and ProgressManager
    if (mergeResult.newDiscovery) {
      this.codexManager.discover(mergeResult.resultElement.defId);
      const stats = this.codexManager.getTotalStats();
      this.progressManager.recordDiscovery(stats.discovered);
    }

    // Lock input during merge + spawn animation
    this.isAnimating = true;

    // Add score and track progress. Prime Time modifier doubles scoring on
    // odd-tier results (Fragment, Crystal, Core, Eternal) — but not the coin
    // award, to keep shop economy independent of run-modifier variance.
    const resultDef = getElementDefOrThrow(mergeResult.resultElement.defId);
    const primeActive = hasModifier(this.activeModifiers, ModifierId.PrimeTime);
    const isOddTier = resultDef.tier % 2 === 1;
    const primeMult = primeActive && isOddTier ? MODIFIER_PRIME_TIME_MULTIPLIER : 1;
    const earnedPoints = mergeResult.pointsEarned * primeMult;
    this.score += earnedPoints;
    this.runMerges++;
    this.progressManager.recordMerge(resultDef.tier);
    this.progressManager.recordScore(earnedPoints);
    this.progressManager.addMergeXP(resultDef.tier);
    this.shopManager.addCoins(TIER_POINTS[resultDef.tier]);

    // Award overcharge for the merge (review fix #1: skill builds the splinter resource)
    this.overchargeSystem.awardMerge();

    // Haptic: tier-proportional impact. High-tier merges feel *heavier*.
    if (resultDef.tier >= Tier.Core) Haptics.heavy();
    else if (resultDef.tier >= Tier.Crystal) Haptics.medium();
    else Haptics.light();

    // Heal nearby same-family elements (review fix #4: positional healing fights the decay spiral)
    this.applyPositionalHealing(targetCol, targetRow, resultDef.family);

    // Track wild merges
    const sourceIsWild = getElementDefOrThrow(sourceElement.defId).family === Family.Wild;
    const targetIsWild = targetElement ? getElementDefOrThrow(targetElement.defId).family === Family.Wild : false;
    if (sourceIsWild || targetIsWild) {
      this.progressManager.recordWildMerge();
    }

    // Check for family unlocks
    const newFamilies = this.familyProgression.checkTierUnlocks(resultDef.tier);

    // Debounced save — cascades coalesce into a single write (issue #26).
    this.scheduleSave();

    // Tutorial advancement is handled in continueChainOrSpawn so that
    // cascade chains (step 4) get to play out fully before the next step
    // mounts. The non-cascade merges in steps 1–3 still land there via
    // the chain-complete branch immediately after this merge settles.

    // Resolve family color for visual feedback
    const familyColor = FAMILY_COLORS[resultDef.family];

    // Capture source UID before animation to use in callback
    const sourceUid = sourceElement.uid;

    // Animate source to target with MERGE-6 compress step (0.8 scale during travel)
    const { x: targetX, y: targetY } = this.cellToPixel(targetCol, targetRow);
    this.tweens.add({
      targets: sourceSprite,
      x: targetX,
      y: targetY,
      scaleX: 0.8,  // MERGE-6: compress step — source squishes to 0.8 while traveling
      scaleY: 0.8,
      duration: MERGE_TWEEN_DURATION,
      ease: Phaser.Math.Easing.Cubic.Out, // VISUAL-IDENTITY.md Section 6.1: "Satisfying deceleration"
      onComplete: () => {
        // Remove source sprite
        this.removeElementSprite(sourceUid);

        // Remove old target sprite (captured before merge modified the grid)
        if (targetUid !== undefined) {
          this.removeElementSprite(targetUid);
        }

        // MERGE-2: Cell background flash (family muted color, 30% opacity, 150ms)
        const cellBg = this.cellBackgrounds[targetCol]?.[targetRow];
        if (cellBg) {
          cellBg.setTint(familyColor);
          cellBg.setAlpha(0.3);
          this.tweens.add({
            targets: cellBg,
            alpha: 1,
            duration: 150,
            ease: Phaser.Math.Easing.Quadratic.Out,
            onComplete: () => {
              cellBg.clearTint();
              cellBg.setAlpha(1);
            },
          });
        }

        // MERGE-1: Particle burst (5 particles, family color, explode then fade)
        this.emitMergeParticles(targetX, targetY, familyColor);

        // MERGE-3: Camera shake for Tier 5+ merges (Core, Primal, Eternal).
        // Originally fired at Tier.Gem (4) but Gems happen many times per run
        // and the constant judder — combined with cascade shakes, overcharge
        // flashes, and corruption shakes — read as the screen "vibrating
        // randomly" on the web build. Reserve shake for genuinely rare events.
        if (resultDef.tier >= Tier.Core) {
          const intensity = 0.001 + (resultDef.tier - 4) * 0.002;
          this.cameras.main.shake(180, intensity);
        }

        // Spawn result sprite with MERGE-6: start at 0.8 compress, scale up with Back.Out
        const resultSprite = this.createElementSprite(mergeResult.resultElement);
        resultSprite.setScale(0.8); // MERGE-6: start from compress point
        this.tweens.add({
          targets: resultSprite,
          scaleX: 1,
          scaleY: 1,
          duration: SPAWN_TWEEN_DURATION,
          ease: Phaser.Math.Easing.Back.Out, // overshoots to ~1.2 then settles at 1.0
          onComplete: () => {
            resultSprite.setDepth(10);
            resultSprite.setAlpha(1);
            resultSprite.setScale(1);

            // Show discovery toast for new elements
            if (mergeResult.newDiscovery) {
              this.showDiscoveryModal(resultDef, familyColor);
            } else {
              MergeToast.show({
                label: `${TIER_NAMES[resultDef.tier]} · ${FAMILY_LABELS[resultDef.family]}`,
                familyColor,
              });
            }

            // Show family unlock toast
            if (newFamilies.length > 0) {
              this.handleFamilyUnlocks(newFamilies);
            }

            // Check for cascade chain
            this.continueChainOrSpawn(targetCol, targetRow, 1);
          },
        });
      },
    });
  }

  /**
   * After a merge result settles, check for cascade or proceed to spawn.
   */
  private continueChainOrSpawn(col: number, row: number, chainStep: number): void {
    this.activeChainStep = chainStep;
    const cascadeTarget = this.chainEngine.findCascadeTarget(col, row);
    if (cascadeTarget) {
      // Chain continues! Animate the cascade merge
      this.time.delayedCall(CHAIN_ANIMATION_DELAY_MS, () => {
        this.performCascadeMerge(col, row, cascadeTarget.col, cascadeTarget.row, chainStep + 1);
      });
    } else {
      // Chain complete — record stats, then spawn
      this.longestChain = Math.max(this.longestChain, chainStep);
      if (chainStep > 1) {
        this.progressManager.recordChain(chainStep);
      }
      // Reset the combo chip once the chain settles.
      this.activeChainStep = 0;
      // Advance the tutorial here (not in performMerge) so the full chain
      // settles before the next step mounts. Covers steps 1–3 (single merge,
      // chainStep=1) and step 4 (cascade, chainStep>=2).
      if (this.tutorialStep > 0 && this.tutorialStep < 5) {
        this.advanceTutorial();
      }
      this.time.delayedCall(SPAWN_DELAY_MS, () => {
        this.postMergeSpawn();
      });
    }
  }

  /**
   * Perform a cascade merge: neighbor element slides into center position.
   */
  private performCascadeMerge(
    centerCol: number,
    centerRow: number,
    neighborCol: number,
    neighborRow: number,
    chainStep: number
  ): void {
    const neighborElement = this.grid.get(neighborCol, neighborRow);
    if (!neighborElement) {
      this.longestChain = Math.max(this.longestChain, chainStep - 1);
      this.postMergeSpawn();
      return;
    }

    const neighborSprite = this.sprites.get(neighborElement.uid);
    if (!neighborSprite) {
      this.longestChain = Math.max(this.longestChain, chainStep - 1);
      this.postMergeSpawn();
      return;
    }

    // Capture center element's UID before merge removes it
    const centerUid = this.grid.get(centerCol, centerRow)?.uid;

    // Execute the merge: neighbor is source, center is target (result at center)
    const mergeResult = this.mergeEngine.merge(neighborCol, neighborRow, centerCol, centerRow);
    if (!mergeResult) {
      this.longestChain = Math.max(this.longestChain, chainStep - 1);
      this.isAnimating = false;
      this.postMergeSpawn();
      return;
    }

    // Track discovery, score with combo multiplier
    if (mergeResult.newDiscovery) {
      this.codexManager.discover(mergeResult.resultElement.defId);
      const stats = this.codexManager.getTotalStats();
      this.progressManager.recordDiscovery(stats.discovered);
    }

    const resultDef = getElementDefOrThrow(mergeResult.resultElement.defId);
    const primeActive = hasModifier(this.activeModifiers, ModifierId.PrimeTime);
    const isOddTier = resultDef.tier % 2 === 1;
    const primeMult = primeActive && isOddTier ? MODIFIER_PRIME_TIME_MULTIPLIER : 1;
    const multipliedPoints = Math.floor(mergeResult.pointsEarned * chainStepMultiplier(chainStep) * primeMult);
    this.score += multipliedPoints;
    this.runMerges++;
    this.progressManager.recordMerge(resultDef.tier);
    this.progressManager.recordScore(multipliedPoints);
    this.progressManager.addMergeXP(resultDef.tier);
    this.shopManager.addCoins(TIER_POINTS[resultDef.tier]);

    // Cascades reward overcharge generously — chains are the main path to splinters.
    // Cascade Surge modifier tops it up with an extra per-step bonus.
    this.overchargeSystem.awardCascade();
    if (hasModifier(this.activeModifiers, ModifierId.CascadeSurge)) {
      this.overchargeSystem.setCharge(
        this.overchargeSystem.getCharge() + MODIFIER_CASCADE_SURGE_OVERCHARGE_BONUS
      );
    }

    // Haptic on every cascade step — feels great during long chains
    Haptics.medium();

    // Heal nearby same-family elements after every cascade merge as well
    this.applyPositionalHealing(centerCol, centerRow, resultDef.family);

    // Check family unlocks (cascade path)
    const newFamiliesFromCascade = this.familyProgression.checkTierUnlocks(resultDef.tier);

    this.scheduleSave();

    // Show combo counter
    this.showComboCounter(centerCol, centerRow, chainStep);

    // Animate neighbor sliding to center
    const familyColor = FAMILY_COLORS[resultDef.family];
    const { x: centerX, y: centerY } = this.cellToPixel(centerCol, centerRow);

    // Stop the neighbor's idle bob so the slide tween owns the y channel.
    this.pauseIdleFloat(neighborElement.uid);

    this.tweens.add({
      targets: neighborSprite,
      x: centerX,
      y: centerY,
      scaleX: 0.8,
      scaleY: 0.8,
      duration: MERGE_TWEEN_DURATION,
      ease: Phaser.Math.Easing.Cubic.Out,
      onComplete: () => {
        // Remove both sprites
        this.removeElementSprite(neighborElement.uid);
        if (centerUid !== undefined) {
          this.removeElementSprite(centerUid);
        }

        // Cell flash
        const cellBg = this.cellBackgrounds[centerCol]?.[centerRow];
        if (cellBg) {
          cellBg.setTint(familyColor);
          cellBg.setAlpha(0.3);
          this.tweens.add({
            targets: cellBg,
            alpha: 1,
            duration: 150,
            ease: Phaser.Math.Easing.Quadratic.Out,
            onComplete: () => { cellBg.clearTint(); cellBg.setAlpha(1); },
          });
        }

        // Particles + camera shake (cascade path — same Tier 5+ gate as the
        // initial merge above so cascades don't strobe the screen).
        this.emitMergeParticles(centerX, centerY, familyColor);
        if (resultDef.tier >= Tier.Core) {
          const intensity = 0.001 + (resultDef.tier - 4) * 0.002;
          this.cameras.main.shake(180, intensity);
        }

        // Spawn result sprite
        const resultSprite = this.createElementSprite(mergeResult.resultElement);
        resultSprite.setScale(0.8);
        this.tweens.add({
          targets: resultSprite,
          scaleX: 1,
          scaleY: 1,
          duration: SPAWN_TWEEN_DURATION,
          ease: Phaser.Math.Easing.Back.Out,
          onComplete: () => {
            resultSprite.setDepth(10);
            resultSprite.setAlpha(1);
            resultSprite.setScale(1);

            if (mergeResult.newDiscovery) {
              this.showDiscoveryModal(resultDef, familyColor);
            } else {
              MergeToast.show({
                label: `${TIER_NAMES[resultDef.tier]} · ${FAMILY_LABELS[resultDef.family]}`,
                familyColor,
              });
            }

            if (newFamiliesFromCascade.length > 0) {
              this.handleFamilyUnlocks(newFamiliesFromCascade);
            }

            // Continue chain
            this.continueChainOrSpawn(centerCol, centerRow, chainStep);
          },
        });
      },
    });
  }

  /**
   * Show floating combo counter during chains.
   */
  private showComboCounter(col: number, row: number, chainStep: number): void {
    const { x, y } = this.cellToPixel(col, row);
    const comboText = this.add.text(x, y - 40, `x${chainStep}!`, {
      fontFamily: 'JetBrains Mono, monospace',
      fontSize: '24px',
      fontStyle: 'bold',
      color: '#FFD600',
      stroke: '#050507',
      strokeThickness: 3,
    })
    .setOrigin(0.5)
    .setDepth(200)
    .setAlpha(0);

    this.tweens.add({
      targets: comboText,
      alpha: 1,
      y: y - 70,
      scaleX: { from: 0.5, to: 1.2 },
      scaleY: { from: 0.5, to: 1.2 },
      duration: 300,
      ease: Phaser.Math.Easing.Back.Out,
      onComplete: () => {
        this.tweens.add({
          targets: comboText,
          alpha: 0,
          y: y - 100,
          duration: 500,
          delay: 300,
          onComplete: () => comboText.destroy(),
        });
      },
    });
  }

  /**
   * Determine how many elements to spawn based on current score.
   */
  private getSpawnCount(): number {
    let count = 1;
    for (const threshold of SPAWN_COUNT_THRESHOLDS) {
      if (this.score >= threshold.score) {
        count = threshold.count;
      }
    }
    if (hasModifier(this.activeModifiers, ModifierId.Frenzy)) {
      count += MODIFIER_FRENZY_SPAWN_BONUS;
    }
    return count;
  }

  /**
   * After a merge (and any future chain), age elements, spawn new fragments, and check game over.
   * Spawn count escalates with score: 1 → 2 → 3, creating rising pressure.
   */
  private postMergeSpawn(): void {
    // Don't spawn or decay during tutorial
    if (this.tutorialStep > 0 && this.tutorialStep < 6) {
      this.isAnimating = false;
      return;
    }

    // Age all elements — newly corrupted ones get visual treatment
    const newlyCorrupted = this.decaySystem.ageAll(this.score);
    for (const corrupted of newlyCorrupted) {
      this.applyCorruptionVisual(corrupted);
    }

    // Update warning visuals for elements approaching corruption
    this.updateDecayWarnings();

    const spawnCount = this.getSpawnCount();
    const spawnedSprites: Phaser.GameObjects.Sprite[] = [];

    for (let i = 0; i < spawnCount; i++) {
      const emptyCell = this.grid.findEmpty();
      if (!emptyCell) break;

      const defId = this.spawnSystem.pickSmartSpawnDefId(this.grid.getAll(), this.score);
      const element = this.spawnElement(defId, emptyCell.col, emptyCell.row);
      const sprite = this.sprites.get(element.uid);
      if (sprite) {
        sprite.setScale(0);
        spawnedSprites.push(sprite);
      }
    }

    if (spawnedSprites.length > 0) {
      // Animate all spawned elements, check game over when last one finishes
      let completed = 0;
      for (const sprite of spawnedSprites) {
        this.tweens.add({
          targets: sprite,
          scaleX: 1,
          scaleY: 1,
          duration: SPAWN_TWEEN_DURATION,
          ease: Phaser.Math.Easing.Back.Out,
          onComplete: () => {
            completed++;
            if (completed >= spawnedSprites.length) {
              this.isAnimating = false;
              this.checkGameOver();
            }
          },
        });
      }
    } else {
      this.isAnimating = false;
      this.checkGameOver();
    }

    this.scheduleSave();
  }

  /**
   * Check if the game is over (no valid merges anywhere).
   */
  private checkGameOver(): void {
    if (this.gameOverDetector.isGameOver()) {
      this.showGameOverOverlay();
    }
  }

  /**
   * Show the game over overlay with stats and new run button.
   * v0.5 — now rendered as a DOM-over-canvas overlay (designer brief Part 1 §5).
   */
  private showGameOverOverlay(): void {
    const isNewRecord = this.score > this.highScore;
    if (isNewRecord) {
      this.highScore = this.score;
    }
    this.runsCompleted++;
    this.progressManager.recordRunScore(this.score);
    this.progressManager.recordRunComplete(this.runsCompleted);
    this.flushSave();

    this.gameOverOverlay = new GameOverOverlay({
      score: this.score,
      highScore: this.highScore,
      runMerges: this.runMerges,
      longestChain: this.longestChain,
      highestTier: this.progressManager.getStats().highestTier,
      isNewRecord,
    });
    this.gameOverOverlay.on('newRun', () => {
      this.gameOverOverlay?.close();
      this.gameOverOverlay = null;
      this.startNewRun();
    });
    this.gameOverOverlay.on('menu', () => {
      this.gameOverOverlay?.close();
      this.gameOverOverlay = null;
      this.flushSave();
      this.scene.start('Menu');
    });
    this.gameOverOverlay.open();
  }

  /**
   * Start a new run: clear the board, keep persistent progress.
   * Persists the cleared state so that scene.restart()'s loadGame() doesn't
   * resurrect the previous board, then re-runs the create() flow which will
   * show the family draft (if needed) and respawn fresh fragments.
   */
  private startNewRun(): void {
    // Clear all sprites and overlays
    for (const [, sprite] of this.sprites) {
      sprite.destroy();
    }
    this.sprites.clear();
    for (const [, overlay] of this.corruptionOverlays) { overlay.destroy(); }
    this.corruptionOverlays.clear();
    for (const [, overlay] of this.warningOverlays) { overlay.destroy(); }
    this.warningOverlays.clear();

    // Clear grid
    this.grid.clear();

    // Reset run state
    this.score = 0;
    this.runMerges = 0;
    this.longestChain = 0;
    this.isAnimating = false;
    this.overchargeSystem.reset();
    this.activeModifiers = [];
    this.splinterUsedThisRun = false;

    // Persist the cleared grid so that scene.restart() → loadGame() doesn't
    // resurrect the previous (game-over) board. Codex / shop / progression are
    // preserved by saveGame (which writes the full snapshot, just with empty
    // gridState). On the next create() the empty grid + score 0 will trigger
    // the draft / fresh-spawn flow. Flush: the scene.restart() immediately
    // below would otherwise tear down our debounced timer mid-flight.
    this.flushSave();

    // Destroy all scene objects and recreate (cleanest way to clear overlay + HUD)
    this.scene.restart();
  }

  /**
   * Positional healing: when a merge completes, all 8-direction neighbors of
   * the result that share the merge result's family are rejuvenated by 1 age.
   * Gives the player agency against the decay spiral (review fix #4) by
   * rewarding tight, family-focused play.
   *
   * Returns the elements that were healed so callers can apply visual feedback.
   */
  private applyPositionalHealing(col: number, row: number, family: Family): ElementInstance[] {
    const healed = this.decaySystem.healNeighbors(col, row, family);
    if (healed.length === 0) return healed;

    const threshold = this.decaySystem.getThreshold(this.score);
    const warningAge = Math.floor(threshold * DECAY_WARNING_RATIO);

    for (const el of healed) {
      // Drop the warning ring if the heal pushed the element back below threshold.
      if (el.age < warningAge) {
        this.removeWarningOverlay(el.uid);
      }
      this.showHealPulse(el);
    }

    return healed;
  }

  /**
   * Brief green pulse on a healed element to acknowledge the positional heal.
   */
  private showHealPulse(element: ElementInstance): void {
    const sprite = this.sprites.get(element.uid);
    if (!sprite) return;
    const { x, y } = this.cellToPixel(element.col, element.row);
    const pulse = this.add.graphics();
    pulse.setDepth(13);
    pulse.lineStyle(2, 0x22FFA7, 0.85); // earth-green to imply restoration
    pulse.strokeCircle(x, y, CELL_SIZE / 2 - 4);

    this.tweens.add({
      targets: pulse,
      alpha: { from: 0.9, to: 0 },
      scaleX: { from: 0.9, to: 1.25 },
      scaleY: { from: 0.9, to: 1.25 },
      duration: 420,
      ease: Phaser.Math.Easing.Quadratic.Out,
      onComplete: () => pulse.destroy(),
    });
  }

  /**
   * Tell the player they need more overcharge before they can splinter.
   */
  private showInsufficientOverchargeToast(): void {
    const cx = GAME_WIDTH / 2;
    const cy = this.gridOffsetY - 60;
    const toast = this.add
      .text(cx, cy, 'Charge your Overcharge bar with merges & chains', {
        font: '600 12px Inter',
        color: '#FFD600',
      })
      .setOrigin(0.5)
      .setDepth(220)
      .setAlpha(0);

    this.tweens.add({
      targets: toast,
      alpha: 1,
      duration: 150,
      yoyo: true,
      hold: 1100,
      onComplete: () => toast.destroy(),
    });
  }

  /**
   * Apply corruption visual to an element that just decayed.
   * Draws a red X overlay and darkens the sprite to clearly indicate a blocker.
   */
  private applyCorruptionVisual(element: ElementInstance): void {
    const sprite = this.sprites.get(element.uid);
    if (!sprite) return;

    // Remove any existing warning overlay
    this.removeWarningOverlay(element.uid);

    // Darken the sprite
    sprite.setTint(0x2A0F30);
    sprite.setAlpha(0.6);

    // Draw X-mark overlay
    const { x, y } = this.cellToPixel(element.col, element.row);
    const overlay = this.add.graphics();
    overlay.setDepth(12);

    // Dark background circle
    overlay.fillStyle(0x0E0718, 0.5);
    overlay.fillCircle(x, y, CELL_SIZE / 2 - 2);

    // Red X mark
    const xSize = 12;
    overlay.lineStyle(3, 0xFF003C, 0.9);
    overlay.beginPath();
    overlay.moveTo(x - xSize, y - xSize);
    overlay.lineTo(x + xSize, y + xSize);
    overlay.stroke();
    overlay.beginPath();
    overlay.moveTo(x + xSize, y - xSize);
    overlay.lineTo(x - xSize, y + xSize);
    overlay.stroke();

    this.corruptionOverlays.set(element.uid, overlay);

    // Subtle pulse on the overlay
    this.tweens.add({
      targets: overlay,
      alpha: { from: 0.7, to: 1.0 },
      duration: 1000,
      yoyo: true,
      repeat: -1,
      ease: Phaser.Math.Easing.Sine.InOut,
    });

    // Show first-corruption explanation toast (once per player)
    if (!this.corruptionToastShown) {
      this.corruptionToastShown = true;
      localStorage.setItem('splintr_corruption_toast', 'done');
      this.showDiscoveryToast(
        'Element Corrupted!',
        'Long-press nearby to splinter & clear it.',
        0xFF003C
      );
    }
  }

  /**
   * Apply a warning visual to an element approaching corruption.
   * Called when age >= 75% of decay threshold.
   */
  private applyDecayWarning(element: ElementInstance): void {
    if (element.corrupted) return;

    const sprite = this.sprites.get(element.uid);
    if (!sprite) return;

    // Don't re-create if already exists
    if (this.warningOverlays.has(element.uid)) return;

    const { x, y } = this.cellToPixel(element.col, element.row);
    const overlay = this.add.graphics();
    overlay.setDepth(11);

    // Pulsing red ring to warn of approaching decay
    overlay.lineStyle(2, 0xFF003C, 0.6);
    overlay.strokeCircle(x, y, CELL_SIZE / 2 + 2);

    this.warningOverlays.set(element.uid, overlay);

    // Pulse the warning ring
    this.tweens.add({
      targets: overlay,
      alpha: { from: 0.3, to: 0.8 },
      duration: 600,
      yoyo: true,
      repeat: -1,
      ease: Phaser.Math.Easing.Sine.InOut,
    });
  }

  /**
   * Remove warning overlay for an element (when it corrupts or is removed).
   */
  private removeWarningOverlay(uid: number): void {
    const overlay = this.warningOverlays.get(uid);
    if (overlay) {
      this.tweens.killTweensOf(overlay);
      overlay.destroy();
      this.warningOverlays.delete(uid);
    }
  }

  /**
   * Remove corruption overlay for an element.
   */
  private removeCorruptionOverlay(uid: number): void {
    const overlay = this.corruptionOverlays.get(uid);
    if (overlay) {
      this.tweens.killTweensOf(overlay);
      overlay.destroy();
      this.corruptionOverlays.delete(uid);
    }
  }

  /**
   * Update decay warnings for all elements on the board.
   */
  private updateDecayWarnings(): void {
    const threshold = this.decaySystem.getThreshold(this.score);
    const warningAge = Math.floor(threshold * DECAY_WARNING_RATIO);

    for (const element of this.grid.getAll()) {
      if (element.corrupted) continue;
      if (element.age >= warningAge) {
        this.applyDecayWarning(element);
      }
    }
  }

  /**
   * Apply corruption visual when loading a saved corrupted element.
   * Also apply warning visuals for elements near threshold.
   */
  private applyCorruptionVisualIfNeeded(element: ElementInstance): void {
    if (element.corrupted) {
      this.applyCorruptionVisual(element);
    } else {
      const threshold = this.decaySystem.getThreshold(this.score);
      const warningAge = Math.floor(threshold * DECAY_WARNING_RATIO);
      if (element.age >= warningAge) {
        this.applyDecayWarning(element);
      }
    }
  }

  /**
   * Emit a burst of 5 particles from a merge point.
   * MERGE-1 (VISUAL-IDENTITY.md Section 6.2): 4–6 particles, family color,
   * explode outward and fade over ~500ms.
   */
  private emitMergeParticles(x: number, y: number, color: number): void {
    const PARTICLE_COUNT = 5;
    const BURST_RADIUS = 40;

    for (let i = 0; i < PARTICLE_COUNT; i++) {
      const angle = (i / PARTICLE_COUNT) * Math.PI * 2;
      const targetX = x + Math.cos(angle) * BURST_RADIUS;
      const targetY = y + Math.sin(angle) * BURST_RADIUS;

      const particle = this.add.circle(x, y, 4, color, 0.85);
      particle.setDepth(20);

      this.tweens.add({
        targets: particle,
        x: targetX,
        y: targetY,
        alpha: 0,
        scaleX: 0.3,
        scaleY: 0.3,
        duration: 480,
        ease: Phaser.Math.Easing.Quadratic.Out,
        onComplete: () => {
          particle.destroy();
        },
      });
    }
  }

  /**
   * Show a discovery toast when a new element is created for the first time.
   */
  /**
   * A new family just tripped its unlock tier. Promote as many as will fit
   * under MAX_ACTIVE_FAMILIES_PER_RUN into the current run's active pool so
   * SpawnSystem actually draws from them, and surface a toast whose message
   * matches what the player will see.
   *
   * The "not auto-added" comment in FamilyProgressionManager was correct when
   * the active pool was already at cap (adding more breaks the 6×6 mergeability
   * math), but it left a silent dead zone when there was room — the player got
   * an "Unlocked!" toast and never saw the family spawn. Split the two cases.
   */
  private handleFamilyUnlocks(newFamilies: Family[]): void {
    const currentActive = this.familyProgression.getActiveFamilies();
    const room = Math.max(0, MAX_ACTIVE_FAMILIES_PER_RUN - currentActive.length);
    const promotedNow = newFamilies.slice(0, room);
    const deferred = newFamilies.slice(promotedNow.length);

    if (promotedNow.length > 0) {
      this.familyProgression.setActiveFamilies([...currentActive, ...promotedNow]);
      this.spawnSystem.invalidateQueue();
    }

    if (promotedNow.length > 0) {
      const names = promotedNow.map(f => FAMILY_LABELS[f]).join(' & ');
      this.showDiscoveryToast(
        `${names} Unlocked!`,
        'New elements will now appear.',
        FAMILY_COLORS[promotedNow[0]],
      );
    }
    if (deferred.length > 0) {
      const names = deferred.map(f => FAMILY_LABELS[f]).join(' & ');
      this.showDiscoveryToast(
        `${names} Unlocked!`,
        'Pick it in your next run’s draft.',
        FAMILY_COLORS[deferred[0]],
      );
    }
  }

  /**
   * Full-screen discovery modal for first-time element discoveries.
   * Falls back to the legacy Phaser toast for family-unlock announcements
   * (which aren't element discoveries — they live on a different code path).
   */
  private showDiscoveryModal(
    resultDef: { name: string; lore: string; tier: Tier; family: Family },
    familyColor: number
  ): void {
    const modal = new DiscoveryModalOverlay({
      name: resultDef.name,
      lore: resultDef.lore,
      tierLabel: TIER_NAMES[resultDef.tier],
      familyLabel: FAMILY_LABELS[resultDef.family],
      familyColor,
    });
    modal.open();
    // Flash the score HUD text so the header also registers the discovery.
    this.tweens.add({
      targets: this.scoreText,
      alpha: 0.3,
      duration: 150,
      yoyo: true,
      repeat: 2,
    });
  }

  private showDiscoveryToast(name: string, lore: string, familyColor: number): void {
    this.discoveryToastQueue.push({ name, lore, familyColor });
    if (!this.discoveryToastActive) {
      this.processDiscoveryToastQueue();
    }
  }

  private processDiscoveryToastQueue(): void {
    const next = this.discoveryToastQueue.shift();
    if (!next) {
      this.discoveryToastActive = false;
      return;
    }
    this.discoveryToastActive = true;
    this.renderDiscoveryToast(next.name, next.lore, next.familyColor);
  }

  private renderDiscoveryToast(name: string, lore: string, familyColor: number): void {
    const centerX = GAME_WIDTH / 2;
    const centerY = this.gridOffsetY / 2;

    // Background panel
    const bg = this.add.graphics();
    bg.setDepth(200);
    bg.fillStyle(0x0B0D15, 0.95);
    bg.fillRoundedRect(centerX - 140, centerY - 36, 280, 72, 12);
    bg.lineStyle(2, familyColor, 0.8);
    bg.strokeRoundedRect(centerX - 140, centerY - 36, 280, 72, 12);

    // "NEW!" badge
    const badge = this.add
      .text(centerX, centerY - 18, 'NEW DISCOVERY!', {
        font: 'bold 12px Inter',
        color: '#' + familyColor.toString(16).padStart(6, '0'),
      })
      .setOrigin(0.5)
      .setDepth(201);

    // Element name
    const nameText = this.add
      .text(centerX, centerY + 2, name, {
        font: 'bold 18px Inter',
        color: '#F5F7FA',
      })
      .setOrigin(0.5)
      .setDepth(201);

    // Lore text (truncated)
    const loreDisplay = lore.length > 40 ? lore.substring(0, 40) + '...' : lore;
    const loreText = this.add
      .text(centerX, centerY + 22, loreDisplay, {
        font: '11px Inter',
        color: '#8892B0',
      })
      .setOrigin(0.5)
      .setDepth(201);

    // Fade in
    const toastElements = [bg, badge, nameText, loreText];
    for (const el of toastElements) {
      el.setAlpha(0);
    }
    this.tweens.add({
      targets: toastElements,
      alpha: 1,
      duration: 200,
      ease: Phaser.Math.Easing.Quadratic.Out,
    });

    // Hold shorter when more toasts are queued so chain discoveries feel snappy
    const holdMs = this.discoveryToastQueue.length > 0 ? 1100 : 2500;
    this.time.delayedCall(holdMs, () => {
      this.tweens.add({
        targets: toastElements,
        alpha: 0,
        duration: 300,
        ease: Phaser.Math.Easing.Quadratic.In,
        onComplete: () => {
          for (const el of toastElements) {
            el.destroy();
          }
          this.processDiscoveryToastQueue();
        },
      });
    });

    // Flash the score HUD text
    this.tweens.add({
      targets: this.scoreText,
      alpha: 0.3,
      duration: 150,
      yoyo: true,
      repeat: 2,
    });
  }

  // ===== TUTORIAL SYSTEM =====

  /**
   * Returns true while a merge/cascade/splinter step is actively waiting on a
   * specific player action — i.e. the welcome panel is dismissed and the
   * tutorial is not yet complete. Guardrail checks in the drag/splinter
   * handlers consult this to decide whether to enforce the allowed-UID sets.
   */
  private isTutorialGuardActive(): boolean {
    return this.tutorialStep > 0 && this.tutorialStep < 6;
  }

  /**
   * Begin the guided tutorial with a welcome screen.
   */
  private startTutorial(): void {
    this.createTutorialOverlay();
    this.showTutorialWelcome();
  }

  /**
   * Pre-spawn the two Fire fragments that step 1 of the tutorial will use.
   *
   * Called synchronously from create() so the very first painted frame of
   * GameScene already shows real elements on the board, instead of an empty
   * grid behind the welcome panel. beginTutorialStep(1) reuses these
   * existing instances rather than respawning over the top of them.
   */
  private preSpawnTutorialStep1(): void {
    if (!this.grid.get(2, 3)) this.spawnElement('fire_fragment', 2, 3);
    if (!this.grid.get(3, 3)) this.spawnElement('fire_fragment', 3, 3);
  }

  /**
   * Run a callback after the next painted frame.
   *
   * Phaser's `time.delayedCall(0, …)` fires on the very next scene update
   * tick, which runs *before* the first render — so any heavy work scheduled
   * that way still blocks the first paint. To actually push work past a
   * paint we need a true rAF→rAF chain: rAF1 fires before paint, schedules
   * rAF2, the browser then paints, then rAF2 fires.
   *
   * Used by create() to keep the welcome overlay / draft modal off the
   * first painted frame of GameScene, so the user sees the live grid the
   * moment they tap PLAY instead of a 1-second black screen.
   *
   * Guarded against scene shutdown so a fast scene swap can't fire the
   * callback on a destroyed scene.
   */
  private afterFirstPaint(fn: () => void): void {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (this.scene.isActive()) {
          fn();
        }
      });
    });
  }

  /**
   * Show welcome/intro screen before the first merge step.
   */
  private showTutorialWelcome(): void {
    const cx = GAME_WIDTH / 2;
    const cy = GAME_HEIGHT * 0.38;

    this.tutorialText = this.add
      .text(cx, cy, 'SPLINTR', {
        font: 'bold 32px Inter',
        color: '#F5F7FA',
      })
      .setOrigin(0.5)
      .setDepth(160)
      .setAlpha(0);

    this.tutorialSubText = this.add
      .text(cx, cy + 44, 'Merge elements to discover new ones', {
        font: '16px Inter',
        color: '#8892B0',
      })
      .setOrigin(0.5)
      .setDepth(161)
      .setAlpha(0);

    // "Start Tutorial" button — bake into a one-shot texture so the
    // rounded-rect path doesn't get re-triangulated every frame while the
    // welcome panel is on screen.
    const btnWidth = 200;
    const btnHeight = 44;
    const btnY = cy + 100;
    const skipBtnY = btnY + 60;

    const startBgKey = `tutorial_start_btn_${btnWidth}x${btnHeight}`;
    if (!this.textures.exists(startBgKey)) {
      const tmp = this.make.graphics({ x: 0, y: 0 }, false);
      tmp.fillStyle(0x00F0FF, 1);
      tmp.fillRoundedRect(0, 0, btnWidth, btnHeight, 8);
      tmp.generateTexture(startBgKey, btnWidth, btnHeight);
      tmp.destroy();
    }
    const startBg = this.add.image(cx, btnY, startBgKey).setOrigin(0.5).setDepth(162);
    startBg.setAlpha(0);

    const startLabel = this.add
      .text(cx, btnY, 'START TUTORIAL', {
        font: '600 16px Inter',
        color: '#050507',
      })
      .setOrigin(0.5)
      .setDepth(163)
      .setAlpha(0);

    const skipLabel = this.add
      .text(cx, skipBtnY, 'Skip', {
        font: '14px Inter',
        color: '#495670',
      })
      .setOrigin(0.5)
      .setDepth(163)
      .setAlpha(0);

    // Fade in — kept short so the panel snaps in instead of crawling.
    // 400ms felt molasses-slow on the Capacitor shell where the renderer
    // sometimes spends the first frames warming pipelines.
    this.tweens.add({
      targets: [this.tutorialText, this.tutorialSubText, startBg, startLabel, skipLabel],
      alpha: 1,
      duration: 180,
      ease: Phaser.Math.Easing.Quadratic.Out,
    });

    const dismissWelcome = (startTutorial: boolean) => {
      // Destroy all welcome UI
      const targets = [this.tutorialText, this.tutorialSubText, startBg, startLabel, skipLabel];
      this.tweens.add({
        targets,
        alpha: 0,
        duration: 200,
        onComplete: () => {
          this.tutorialText?.destroy();
          this.tutorialSubText?.destroy();
          this.tutorialText = null;
          this.tutorialSubText = null;
          startBg.destroy();
          startLabel.destroy();
          skipLabel.destroy();

          if (startTutorial) {
            this.tutorialWelcomeDismissed = true;
            this.beginTutorialStep(1);
          } else {
            // Skip tutorial: mark done and start normal game
            this.tutorialStep = 6;
            localStorage.setItem('splintr_tutorial', 'done');
            this.clearTutorialVisuals();
            for (let i = 0; i < 6; i++) {
              const emptyCell = this.grid.findEmpty();
              if (emptyCell) {
                this.spawnElement(this.spawnSystem.pickSpawnDefId(), emptyCell.col, emptyCell.row);
              }
            }
          }
        },
      });
    };

    // Make start button interactive
    const startZone = this.add
      .zone(cx, btnY, btnWidth, btnHeight)
      .setDepth(175)
      .setInteractive();
    startZone.once('pointerup', () => {
      startZone.destroy();
      skipZone.destroy();
      dismissWelcome(true);
    });

    // Make skip interactive
    const skipZone = this.add
      .zone(cx, skipBtnY, btnWidth, btnHeight)
      .setDepth(175)
      .setInteractive();
    skipZone.once('pointerup', () => {
      startZone.destroy();
      skipZone.destroy();
      dismissWelcome(false);
    });
  }

  /**
   * Set up a tutorial merge step: spawn elements, spotlight, arrow, hint,
   * and populate the allowed-UID guardrails so only these two elements can
   * be dragged/dropped this step.
   *
   * Steps 1 and 2 teach the basic merge (two Fragments → Shard). Step 3
   * teaches the tier-up (two Shards → Crystal) using the Shards already on
   * the board from steps 1 + 2, so the player sees progression instead of
   * yet another fragment pair. Step 4 is handled separately by
   * beginCascadeTutorialStep (it needs a bespoke board layout).
   */
  private beginTutorialStep(step: number): void {
    if (step === 4) {
      this.beginCascadeTutorialStep();
      return;
    }

    interface StepConfig {
      cells: [number, number][];
      defId: string;
      hint: string;
      sub?: string;
    }
    const steps: Record<number, StepConfig> = {
      1: {
        cells: [[2, 3], [3, 3]],
        defId: 'fire_fragment',
        hint: 'Drag matching elements together',
        sub: 'Two Fragments merge into a Shard.',
      },
      2: {
        // Keep this pair far from step 1's Shard so no accidental cascade
        // fires before we introduce chains explicitly in step 4.
        cells: [[2, 1], [3, 1]],
        defId: 'fire_fragment',
        hint: 'Merge these two as well',
        sub: 'Now you have two Shards on the board.',
      },
      3: {
        // No explicit cells — the pair is whatever Shards are already on
        // the grid from steps 1 and 2. Populated dynamically below.
        cells: [],
        defId: 'fire_shard',
        hint: 'Merge the two Shards',
        sub: 'Same tier + same family = next tier. Crystals can be splintered!',
      },
    };

    const cfg = steps[step];
    if (!cfg) return;

    let cellA: [number, number];
    let cellB: [number, number];
    let elA: ElementInstance;
    let elB: ElementInstance;

    if (step === 3) {
      // Find the two Shards the player produced in steps 1 + 2. If for any
      // reason they are missing (defensive — shouldn't happen), fall back to
      // spawning a fresh pair so the tutorial can still complete.
      const shards = this.grid.getAll().filter((el) => el.defId === cfg.defId);
      if (shards.length >= 2) {
        elA = shards[0];
        elB = shards[1];
      } else {
        cellA = [2, 3];
        cellB = [3, 3];
        elA = this.grid.get(cellA[0], cellA[1]) ?? this.spawnElement(cfg.defId, cellA[0], cellA[1]);
        elB = this.grid.get(cellB[0], cellB[1]) ?? this.spawnElement(cfg.defId, cellB[0], cellB[1]);
      }
      cellA = [elA.col, elA.row];
      cellB = [elB.col, elB.row];
    } else {
      cellA = cfg.cells[0];
      cellB = cfg.cells[1];
      elA = this.grid.get(cellA[0], cellA[1]) ?? this.spawnElement(cfg.defId, cellA[0], cellA[1]);
      elB = this.grid.get(cellB[0], cellB[1]) ?? this.spawnElement(cfg.defId, cellB[0], cellB[1]);
    }

    const sprA = this.sprites.get(elA.uid);
    const sprB = this.sprites.get(elB.uid);
    if (sprA) sprA.setDepth(155);
    if (sprB) sprB.setDepth(155);

    // Guardrail: only these two UIDs can be dragged or dropped on.
    this.tutorialAllowedSourceUids = new Set([elA.uid, elB.uid]);
    this.tutorialAllowedTargetUids = new Set([elA.uid, elB.uid]);
    this.tutorialAllowedSplinterUid = null;

    // Add spotlights and glow rings on the two cells
    this.addTutorialSpotlight(cellA[0], cellA[1]);
    this.addTutorialSpotlight(cellB[0], cellB[1]);

    // Animated arrow between elements
    this.showTutorialArrow(cellA[0], cellA[1], cellB[0], cellB[1]);

    // Instruction text above the action
    const midY = (this.cellToPixel(cellA[0], cellA[1]).y + this.cellToPixel(cellB[0], cellB[1]).y) / 2;
    this.showTutorialHint(cfg.hint, cfg.sub, midY - 90);
  }

  /**
   * Tutorial step 4: teach cascade chains. Clears the board and arranges a
   * layout where the player's next merge lands adjacent to a matching Shard,
   * firing an automatic chain → Crystal. The cascade chain plays out with
   * its normal multiplier, giving the player a live demonstration of why
   * chains are worth chasing.
   */
  private beginCascadeTutorialStep(): void {
    // Clear every existing element so the layout is deterministic.
    const existing = this.grid.getAll().map((el) => ({ uid: el.uid, col: el.col, row: el.row }));
    for (const { uid, col, row } of existing) {
      this.grid.remove(col, row);
      this.removeElementSprite(uid);
    }

    // Layout: Fragments at columns 1 and 3 of row 3, bracketing an existing
    // Shard at (2, 3). Whichever direction the player drags, the merged Shard
    // lands on a cell adjacent (diagonally or orthogonally) to the existing
    // Shard, triggering an 8-neighbour cascade.
    const fragA = this.spawnElement('fire_fragment', 1, 3);
    const fragB = this.spawnElement('fire_fragment', 3, 3);
    const shard = this.spawnElement('fire_shard', 2, 3);

    const fragASpr = this.sprites.get(fragA.uid);
    const fragBSpr = this.sprites.get(fragB.uid);
    const shardSpr = this.sprites.get(shard.uid);
    if (fragASpr) fragASpr.setDepth(155);
    if (fragBSpr) fragBSpr.setDepth(155);
    if (shardSpr) shardSpr.setDepth(155);

    // Guardrail: only the two fragments may move; the shard stays put so
    // the cascade target is guaranteed.
    this.tutorialAllowedSourceUids = new Set([fragA.uid, fragB.uid]);
    this.tutorialAllowedTargetUids = new Set([fragA.uid, fragB.uid]);
    this.tutorialAllowedSplinterUid = null;

    // Spotlights on all three cells; glow only on the pair that moves, so
    // the eye follows the drag, not the stationary Shard.
    this.addTutorialSpotlight(1, 3);
    this.addTutorialSpotlight(3, 3);
    this.addTutorialSpotlight(2, 3);
    this.showTutorialArrow(1, 3, 3, 3);

    const midY = this.cellToPixel(2, 3).y;
    this.showTutorialHint(
      'Chain a cascade',
      'Land a merge next to a match — chains multiply your score.',
      midY - 100
    );
  }

  /**
   * Advance the tutorial after a merge or splinter completes.
   *
   * Steps: 1 (first merge) → 2 (second merge) → 3 (tier-up merge) →
   * 4 (cascade demo) → 5 (splinter demo) → 6 (complete, free play).
   */
  private advanceTutorial(): void {
    if (this.tutorialStep >= 6) return;

    this.tutorialStep++;

    // Clear the allowed-UID sets so no drags are possible during the brief
    // transition window — they'll be repopulated by the next step's setup.
    this.tutorialAllowedSourceUids = new Set();
    this.tutorialAllowedTargetUids = new Set();
    this.tutorialAllowedSplinterUid = null;

    // Clear visuals from the previous step
    this.clearTutorialVisuals();

    if (this.tutorialStep === 2 || this.tutorialStep === 3 || this.tutorialStep === 4) {
      // Brief pause for the merge (+ any cascade) to settle, then mount the
      // next step. Step 3 reuses the Shards already on the board; step 4
      // clears and re-seeds a cascade-ready layout.
      this.time.delayedCall(500, () => {
        this.createTutorialOverlay();
        this.beginTutorialStep(this.tutorialStep);
      });
    } else if (this.tutorialStep === 5) {
      // Splinter tutorial — spawn a Crystal and teach long-press. The
      // cascade chain from step 4 produces a Crystal we can reuse; if for
      // any reason there isn't one, beginSplinterTutorial spawns a fresh
      // Crystal in an empty cell.
      this.time.delayedCall(800, () => {
        this.beginSplinterTutorial();
      });
    } else if (this.tutorialStep === 6) {
      // Tutorial complete
      localStorage.setItem('splintr_tutorial', 'done');

      // Spawn remaining fragments to fill the starting board
      for (let i = 0; i < 4; i++) {
        const emptyCell = this.grid.findEmpty();
        if (emptyCell) {
          this.spawnElement(getRandomFragment().id, emptyCell.col, emptyCell.row);
        }
      }

      // Tutorial is over — mount the idle bob on every live tile so the
      // board feels alive the moment the player takes control. (Tiles
      // created via spawnElement above already auto-attach through
      // createElementSprite, but any pre-tutorial board tiles were gated
      // on tutorialStep < 6 and need a retroactive mount.)
      this.mountIdleFloatAll();

      // Show completion message (no overlay)
      const cx = GAME_WIDTH / 2;
      const doneText = this.add
        .text(cx, GAME_HEIGHT * 0.35, 'Tutorial Complete!', {
          font: 'bold 24px Inter',
          color: '#F5F7FA',
        })
        .setOrigin(0.5)
        .setDepth(160)
        .setAlpha(0);

      const doneSub = this.add
        .text(cx, GAME_HEIGHT * 0.35 + 36, 'Discover all 56 elements. Good luck!', {
          font: '14px Inter',
          color: '#8892B0',
        })
        .setOrigin(0.5)
        .setDepth(161)
        .setAlpha(0);

      this.tweens.add({
        targets: [doneText, doneSub],
        alpha: 1,
        duration: 300,
        ease: Phaser.Math.Easing.Quadratic.Out,
      });
      this.time.delayedCall(2500, () => {
        this.tweens.add({
          targets: [doneText, doneSub],
          alpha: 0,
          duration: 300,
          onComplete: () => {
            doneText.destroy();
            doneSub.destroy();
          },
        });
      });
    }
  }

  /**
   * Tutorial step 5: teach the splinter (long-press) mechanic.
   *
   * Re-uses the Crystal produced by the step-4 cascade chain when possible
   * (the expected board state); spawns a fresh Crystal otherwise. Highlights
   * both the Crystal itself and the Overcharge bar above the grid so the
   * player learns the resource that gates splintering. The hint explains
   * the cost (50 Overcharge) and the result (2 same-family charged
   * Shards) — the two things a player actually needs to decide *when* to
   * splinter.
   */
  private beginSplinterTutorial(): void {
    this.createTutorialOverlay();

    // Prefer the Crystal left on the board by the cascade step.
    let crystal = this.grid.getAll().find((el) => el.defId === 'fire_crystal');
    if (!crystal) {
      const emptyCell = this.grid.findEmpty();
      if (!emptyCell) return;
      crystal = this.spawnElement('fire_crystal', emptyCell.col, emptyCell.row);
    }

    const spr = this.sprites.get(crystal.uid);
    if (spr) spr.setDepth(155);

    // Guardrail: nothing is draggable, and only this specific Crystal can be
    // splintered. Dragging the Crystal is blocked by the empty source set.
    this.tutorialAllowedSourceUids = new Set();
    this.tutorialAllowedTargetUids = new Set();
    this.tutorialAllowedSplinterUid = crystal.uid;

    // Spotlight on the crystal
    this.addTutorialSpotlight(crystal.col, crystal.row);

    // Highlight the Overcharge bar so the player learns what pays the cost.
    this.addTutorialOverchargeHighlight();

    // Two-line hint: what to do + what it costs + what it produces.
    const { y: cellY } = this.cellToPixel(crystal.col, crystal.row);
    this.showTutorialHint(
      'Hold the Crystal to splinter',
      'Costs 50 Overcharge — breaks it into 2 charged Shards that can spark a cascade.',
      cellY - 110
    );
  }

  /**
   * Draw a pulsing ring around the Overcharge bar so the player knows which
   * meter pays the splinter cost. Mirrors addTutorialSpotlight's visual
   * language (pulsing family-coloured ring) and is cleaned up by
   * clearTutorialVisuals() via the shared glow/spotlight tracking.
   */
  private addTutorialOverchargeHighlight(): void {
    const barW = 240;
    const barH = 18;
    const barX = (GAME_WIDTH - barW) / 2;
    const barY = this.gridOffsetY - 34;
    const pad = 6;

    const ring = this.add.graphics();
    ring.setDepth(152);
    // Cyan accent matches the OVERCHARGE label colour (config/HUD).
    ring.lineStyle(2, 0x00F0FF, 0.9);
    ring.strokeRoundedRect(barX - pad, barY - pad, barW + pad * 2, barH + pad * 2, 12);
    this.tutorialGlows.push(ring);
    this.tutorialOverchargeHighlight = ring;

    this.tweens.add({
      targets: ring,
      alpha: { from: 0.4, to: 1 },
      duration: 700,
      ease: Phaser.Math.Easing.Sine.InOut,
      yoyo: true,
      repeat: -1,
    });
  }

  /**
   * Create full-screen dimming overlay for the tutorial.
   */
  private createTutorialOverlay(): void {
    if (this.tutorialOverlay) return;
    this.tutorialOverlay = this.add.graphics();
    this.tutorialOverlay.setDepth(150);
    this.tutorialOverlay.fillStyle(0x050507, 0.82);
    this.tutorialOverlay.fillRect(0, 0, GAME_WIDTH, GAME_HEIGHT);
    // Make it interactive to block clicks on the grid below
    this.tutorialOverlay.setInteractive(
      new Phaser.Geom.Rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT),
      Phaser.Geom.Rectangle.Contains
    );
  }

  /**
   * Add a spotlight cutout + pulsing glow ring on a grid cell.
   */
  private addTutorialSpotlight(col: number, row: number): void {
    const { x, y } = this.cellToPixel(col, row);
    const half = CELL_SIZE / 2;
    const pad = 4;

    // Spotlight: bright cell background above the dim overlay
    const spot = this.add.graphics();
    spot.setDepth(151);
    spot.fillStyle(0x0F1115, 1);
    spot.fillRoundedRect(x - half - pad, y - half - pad, CELL_SIZE + pad * 2, CELL_SIZE + pad * 2, 10);
    this.tutorialSpotlights.push(spot);

    // Pulsing glow ring in fire family color
    const glow = this.add.graphics();
    glow.setDepth(152);
    glow.lineStyle(2, 0xFF5E2A, 0.6); // Fire family color
    glow.strokeRoundedRect(x - half - pad, y - half - pad, CELL_SIZE + pad * 2, CELL_SIZE + pad * 2, 10);
    this.tutorialGlows.push(glow);

    this.tweens.add({
      targets: glow,
      alpha: { from: 0.3, to: 0.9 },
      duration: 800,
      ease: Phaser.Math.Easing.Sine.InOut,
      yoyo: true,
      repeat: -1,
    });
  }

  /**
   * Draw an animated arrow between two grid cells.
   */
  private showTutorialArrow(fromCol: number, fromRow: number, toCol: number, toRow: number): void {
    if (this.tutorialArrow) {
      this.tutorialArrow.destroy();
    }

    const from = this.cellToPixel(fromCol, fromRow);
    const to = this.cellToPixel(toCol, toRow);

    // Arrow sits between the two cells
    const midX = (from.x + to.x) / 2;
    const midY = (from.y + to.y) / 2;
    const angle = Math.atan2(to.y - from.y, to.x - from.x);
    const len = 20;
    const headSize = 8;

    this.tutorialArrow = this.add.graphics();
    this.tutorialArrow.setDepth(170);

    // Draw arrow line
    this.tutorialArrow.lineStyle(3, 0xF5F7FA, 0.7);
    this.tutorialArrow.beginPath();
    this.tutorialArrow.moveTo(midX - Math.cos(angle) * len, midY - Math.sin(angle) * len);
    this.tutorialArrow.lineTo(midX + Math.cos(angle) * len, midY + Math.sin(angle) * len);
    this.tutorialArrow.strokePath();

    // Draw arrowhead
    const tipX = midX + Math.cos(angle) * len;
    const tipY = midY + Math.sin(angle) * len;
    this.tutorialArrow.fillStyle(0xF5F7FA, 0.7);
    this.tutorialArrow.beginPath();
    this.tutorialArrow.moveTo(tipX, tipY);
    this.tutorialArrow.lineTo(
      tipX - Math.cos(angle - 0.5) * headSize,
      tipY - Math.sin(angle - 0.5) * headSize
    );
    this.tutorialArrow.lineTo(
      tipX - Math.cos(angle + 0.5) * headSize,
      tipY - Math.sin(angle + 0.5) * headSize
    );
    this.tutorialArrow.closePath();
    this.tutorialArrow.fillPath();

    // Nudge animation in drag direction
    const nudgeDist = 6;
    this.tweens.add({
      targets: this.tutorialArrow,
      x: Math.cos(angle) * nudgeDist,
      duration: 700,
      ease: Phaser.Math.Easing.Sine.InOut,
      yoyo: true,
      repeat: -1,
    });
  }

  /**
   * Show large centered tutorial instruction text.
   */
  private showTutorialHint(text: string, subText?: string, yPos?: number): void {
    this.clearTutorialHintText();
    const cx = GAME_WIDTH / 2;
    const y = yPos ?? GAME_HEIGHT * 0.3;

    this.tutorialText = this.add
      .text(cx, y, text, {
        font: 'bold 22px Inter',
        color: '#F5F7FA',
        align: 'center',
        wordWrap: { width: 350 },
      })
      .setOrigin(0.5)
      .setDepth(160)
      .setAlpha(0);

    this.tweens.add({
      targets: this.tutorialText,
      alpha: 1,
      duration: 300,
      ease: Phaser.Math.Easing.Quadratic.Out,
    });

    if (subText) {
      this.tutorialSubText = this.add
        .text(cx, y + 32, subText, {
          font: '14px Inter',
          color: '#8892B0',
          align: 'center',
          wordWrap: { width: 350 },
        })
        .setOrigin(0.5)
        .setDepth(161)
        .setAlpha(0);

      this.tweens.add({
        targets: this.tutorialSubText,
        alpha: 1,
        duration: 300,
        delay: 100,
        ease: Phaser.Math.Easing.Quadratic.Out,
      });
    }
  }

  /**
   * Destroy only the tutorial hint text objects.
   */
  private clearTutorialHintText(): void {
    if (this.tutorialText) {
      this.tutorialText.destroy();
      this.tutorialText = null;
    }
    if (this.tutorialSubText) {
      this.tutorialSubText.destroy();
      this.tutorialSubText = null;
    }
  }

  /**
   * Destroy all tutorial visual objects (overlay, spotlights, glows, arrow, text).
   */
  private clearTutorialVisuals(): void {
    if (this.tutorialOverlay) {
      this.tutorialOverlay.destroy();
      this.tutorialOverlay = null;
    }
    for (const s of this.tutorialSpotlights) s.destroy();
    this.tutorialSpotlights = [];
    // Glows carry infinite yoyo tweens (addTutorialSpotlight). Destroying the
    // Graphics target does not remove the tween from the tween manager; it
    // keeps running with a dead target. Kill explicitly to avoid the leak.
    for (const g of this.tutorialGlows) {
      this.tweens.killTweensOf(g);
      g.destroy();
    }
    this.tutorialGlows = [];
    // The overcharge bar highlight (if any) is pushed onto tutorialGlows by
    // addTutorialOverchargeHighlight, so it's destroyed above; just clear
    // the cached reference.
    this.tutorialOverchargeHighlight = null;
    if (this.tutorialArrow) {
      this.tweens.killTweensOf(this.tutorialArrow);
      this.tutorialArrow.destroy();
      this.tutorialArrow = null;
    }
    this.clearTutorialHintText();
  }

  /**
   * Show an overlay displaying offline idle rewards.
   */
  private showOfflineRewardsOverlay(count: number, durationMs: number): void {
    const centerX = GAME_WIDTH / 2;
    const centerY = 300;

    // Format duration
    const hours = Math.floor(durationMs / 3600000);
    const minutes = Math.floor((durationMs % 3600000) / 60000);
    const timeStr = hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;

    // Background overlay
    const overlay = this.add.graphics();
    overlay.setDepth(300);
    overlay.fillStyle(0x0A0B11, 0.85);
    overlay.fillRoundedRect(centerX - 130, centerY - 55, 260, 110, 16);
    overlay.lineStyle(2, 0x00F0FF, 0.6);
    overlay.strokeRoundedRect(centerX - 130, centerY - 55, 260, 110, 16);

    const title = this.add
      .text(centerX, centerY - 30, 'Welcome Back!', {
        font: 'bold 18px Inter',
        color: '#F5F7FA',
      })
      .setOrigin(0.5)
      .setDepth(301);

    const detail = this.add
      .text(centerX, centerY, `${count} fragments generated in ${timeStr}`, {
        font: '14px Inter',
        color: '#8892B0',
      })
      .setOrigin(0.5)
      .setDepth(301);

    const tapHint = this.add
      .text(centerX, centerY + 30, 'Tap to continue', {
        font: '12px Inter',
        color: '#495670',
      })
      .setOrigin(0.5)
      .setDepth(301);

    const elements = [overlay, title, detail, tapHint];

    // Dismiss on tap anywhere
    const dismissZone = this.add.zone(centerX, centerY, 260, 110)
      .setDepth(302)
      .setInteractive();

    dismissZone.once('pointerup', () => {
      for (const el of elements) {
        el.destroy();
      }
      dismissZone.destroy();
    });

    // Also auto-dismiss after 5 seconds
    this.time.delayedCall(5000, () => {
      if (dismissZone.active) {
        for (const el of elements) {
          el.destroy();
        }
        dismissZone.destroy();
      }
    });
  }

  /**
   * Mount the CRT-style scanline overlay above the Phaser canvas.
   * Sibling of the canvas (not inside dom-overlay-root), so it layers
   * below DOM overlays (toasts, modals) but above the WebGL layer.
   */
  private mountScanline(): void {
    if (this.scanlineEl) return;
    const el = document.createElement('div');
    el.className = 'scanline-overlay';
    el.setAttribute('aria-hidden', 'true');
    const host = document.getElementById('game') ?? document.body;
    host.appendChild(el);
    this.scanlineEl = el;
  }

  private unmountScanline(): void {
    this.scanlineEl?.remove();
    this.scanlineEl = null;
  }

  /**
   * Open the DOM pause modal. Gates spawn/idle timers and drag input until
   * the player resumes. Idle-float tweens are paused on open, resumed on close.
   */
  private openPauseOverlay(): void {
    if (this.isPaused) return;
    this.isPaused = true;
    this.pauseStartedAt = performance.now();
    this.tweens.pauseAll();
    this.input.enabled = false;

    const overlay = new PauseOverlay({
      score: this.score,
      combo: this.activeChainStep,
      elapsedMs: Math.max(0, (this.pauseStartedAt - this.runStartMs) - this.pausedElapsedMs),
    });
    this.pauseOverlay = overlay;

    overlay.on('resume', () => this.closePauseOverlay(overlay));
    overlay.on('menu', () => {
      this.closePauseOverlay(overlay);
      this.flushSave();
      this.scene.start('Menu');
    });
    overlay.open();
  }

  private closePauseOverlay(overlay: PauseOverlay): void {
    if (!this.isPaused) return;
    this.isPaused = false;
    if (this.pauseStartedAt !== null) {
      this.pausedElapsedMs += performance.now() - this.pauseStartedAt;
      this.pauseStartedAt = null;
    }
    this.tweens.resumeAll();
    this.input.enabled = true;
    overlay.close();
    if (this.pauseOverlay === overlay) this.pauseOverlay = null;
  }

  /**
   * Cancel any in-progress splinter long-press.
   */
  private cancelSplinterCharge(): void {
    if (this.splinterTimer) {
      this.splinterTimer.destroy();
      this.splinterTimer = null;
    }
    this.cancelSplinterGlow();
    this.splinterTarget = null;
  }

  private cancelSplinterGlow(): void {
    if (this.splinterGlow) {
      this.splinterGlow.destroy();
      this.splinterGlow = null;
    }
  }

  /**
   * Handle splinter on an element (triggered after long-press completes).
   * Costs overcharge (built up by gameplay). Removes adjacent corrupted elements (splash damage).
   */
  private handleSplinterTap(element: ElementInstance): void {
    if (!this.mergeEngine.canSplinter(element)) {
      return;
    }

    const inTutorial = this.tutorialStep > 0 && this.tutorialStep < 6;
    // First Break Free modifier waives the overcharge cost on the run's first splinter.
    const freeSplinter =
      !inTutorial &&
      !this.splinterUsedThisRun &&
      hasModifier(this.activeModifiers, ModifierId.FreeFirstSplinter);

    // Splinter costs overcharge (skip during tutorial — players are still learning).
    if (!inTutorial && !freeSplinter && !this.overchargeSystem.canSplinter()) {
      this.showInsufficientOverchargeToast();
      return;
    }

    // Capture the source position before splinter removes the element
    const sourceCol = element.col;
    const sourceRow = element.row;

    const splinterResult = this.mergeEngine.splinter(sourceCol, sourceRow);
    if (!splinterResult) {
      return;
    }

    // Spend overcharge (skip during tutorial or when the free-splinter modifier waives it)
    if (!inTutorial && !freeSplinter) {
      this.overchargeSystem.spendSplinter();
    }
    if (!inTutorial) {
      this.splinterUsedThisRun = true;
    }

    // Splinter is the signature "heavy" ability — always fire a heavy haptic
    Haptics.heavy();

    // Track splinter and save
    this.progressManager.recordSplinter();

    // Remove source sprite
    this.removeElementSprite(element.uid);

    // Splash damage: destroy adjacent corrupted elements
    if (!inTutorial) {
      const neighbors = this.grid.getNeighbors8(sourceCol, sourceRow);
      for (const neighbor of neighbors) {
        if (neighbor.element.corrupted) {
          this.removeElementSprite(neighbor.element.uid);
          this.grid.remove(neighbor.col, neighbor.row);
          // Flash the cleared cell
          const cellBg = this.cellBackgrounds[neighbor.col]?.[neighbor.row];
          if (cellBg) {
            cellBg.setTint(0x8A00FF);
            cellBg.setAlpha(0.4);
            this.tweens.add({
              targets: cellBg,
              alpha: 1,
              duration: 200,
              onComplete: () => { cellBg.clearTint(); cellBg.setAlpha(1); },
            });
          }
        }
      }
    }

    // Create sprites for fragments (MergeEngine places them in the grid but doesn't create sprites)
    for (const fragment of splinterResult.fragments) {
      this.createElementSprite(fragment);
    }

    // Animate same-family fragments scattering from source position. Fragments
    // are "charged": once they've landed, we check each one for a cascade
    // target (same as a merge result would), so a well-placed splinter can
    // trigger a chain instead of just reshuffling the board.
    let scatterCompleted = 0;
    const totalFragments = splinterResult.fragments.length;

    const onAllScattered = () => {
      if (!inTutorial) this.tryChargedSplinterCascade(splinterResult.fragments);
    };

    for (const fragment of splinterResult.fragments) {
      const sprite = this.sprites.get(fragment.uid);
      if (!sprite) {
        scatterCompleted++;
        if (scatterCompleted >= totalFragments) onAllScattered();
        continue;
      }
      const { x: startX, y: startY } = this.cellToPixel(sourceCol, sourceRow);
      const { x: targetX, y: targetY } = this.cellToPixel(fragment.col, fragment.row);

      sprite.setPosition(startX, startY);
      sprite.setScale(0.5);

      // Subtle cyan "charged" glow while the fragment travels — gets cleared
      // by the cascade path (if it fires) or fades naturally with the sprite.
      this.emitChargedFragmentSparkle(startX, startY);

      this.tweens.add({
        targets: sprite,
        x: targetX,
        y: targetY,
        scaleX: 1,
        scaleY: 1,
        duration: SPLINTER_TWEEN_DURATION,
        ease: Phaser.Math.Easing.Expo.Out,
        onComplete: () => {
          sprite.setDepth(inTutorial ? 155 : 10);
          scatterCompleted++;
          if (scatterCompleted >= totalFragments) onAllScattered();
        },
      });
    }

    this.scheduleSave();

    // Advance tutorial after splinter step
    if (this.tutorialStep === 5) {
      this.advanceTutorial();
    }
  }

  /**
   * After splinter fragments have finished scattering, walk them in order and
   * trigger a cascade chain at the first one that has a valid adjacent merge
   * target. Only one chain fires per splinter — chaining more than one would
   * need a serialisation layer the merge flow doesn't have, and "any fragment
   * can spark a chain" already delivers the design goal.
   */
  private tryChargedSplinterCascade(fragments: ElementInstance[]): void {
    for (const fragment of fragments) {
      const current = this.grid.get(fragment.col, fragment.row);
      if (!current || current.uid !== fragment.uid) continue;
      const target = this.chainEngine.findCascadeTarget(fragment.col, fragment.row);
      if (!target) continue;
      // Lock input (the cascade path will unlock it via postMergeSpawn) and
      // kick off the chain starting from this fragment — treated as chain
      // step 1, so the adjacent match becomes chain step 2 (x1.5 multiplier).
      this.isAnimating = true;
      this.continueChainOrSpawn(fragment.col, fragment.row, 1);
      return;
    }
  }

  /**
   * Quick cyan sparkle at the splinter source — signals that the fragments
   * are "charged" and ready to chain. Cheap: 4 small circles fading outward.
   */
  private emitChargedFragmentSparkle(x: number, y: number): void {
    for (let i = 0; i < 4; i++) {
      const angle = (i / 4) * Math.PI * 2;
      const p = this.add.circle(x, y, 2, 0x00F0FF, 0.9).setDepth(140);
      this.tweens.add({
        targets: p,
        x: x + Math.cos(angle) * 18,
        y: y + Math.sin(angle) * 18,
        alpha: 0,
        duration: 320,
        ease: Phaser.Math.Easing.Quadratic.Out,
        onComplete: () => p.destroy(),
      });
    }
  }

  /**
   * Modal overlay shown when the stored save cannot be parsed (issue #25).
   * The player chooses between starting fresh (which wipes the save) or
   * copying the raw stored bytes so they can send them to support. Until the
   * player picks one, the scene does nothing — we refuse to silently
   * overwrite potentially-recoverable state.
   */
  private showSaveRecoveryOverlay(): void {
    const cx = GAME_WIDTH / 2;
    const cy = GAME_HEIGHT / 2;

    // Re-read to capture the raw string for the copy affordance. The initial
    // boot read already failed; we only need `raw` here.
    const rawResult = SaveManager.readLocal();
    const raw = !rawResult.ok ? rawResult.raw ?? '' : '';

    // Dimmed backdrop captures all input so nothing behind the modal reacts.
    const backdrop = this.add.rectangle(cx, cy, GAME_WIDTH, GAME_HEIGHT, 0x050507, 0.88)
      .setDepth(300)
      .setInteractive();

    const panelW = 320;
    const panelH = 220;
    const panel = this.add.graphics().setDepth(301);
    panel.fillStyle(0x0B0D15, 0.98);
    panel.fillRoundedRect(cx - panelW / 2, cy - panelH / 2, panelW, panelH, 14);
    panel.lineStyle(2, 0xFF003C, 0.9);
    panel.strokeRoundedRect(cx - panelW / 2, cy - panelH / 2, panelW, panelH, 14);

    const title = this.add.text(cx, cy - panelH / 2 + 28, 'SAVE COULD NOT BE LOADED', {
      font: 'bold 14px "Space Grotesk"',
      color: '#FF003C',
    }).setOrigin(0.5).setDepth(302);

    const body = this.add.text(cx, cy - 14, 'Your save file appears to be corrupted.\nStart fresh, or copy the raw save first\nso we can try to recover it.', {
      font: '500 12px Inter',
      color: '#F5F7FA',
      align: 'center',
    }).setOrigin(0.5).setDepth(302);

    // Primary action — reset and start fresh.
    const resetBtn = this.add.graphics().setDepth(302);
    resetBtn.fillStyle(0xFF003C, 1);
    resetBtn.fillRoundedRect(cx - 140, cy + 48, 130, 44, 10);
    const resetLabel = this.add.text(cx - 75, cy + 70, 'Reset & Start Fresh', {
      font: 'bold 12px "Space Grotesk"',
      color: '#F5F7FA',
    }).setOrigin(0.5).setDepth(303);
    const resetZone = this.add.zone(cx - 75, cy + 70, 130, 44)
      .setInteractive({ useHandCursor: true })
      .setDepth(304);

    // Secondary action — copy raw bytes so the player can send them to us.
    const copyBtn = this.add.graphics().setDepth(302);
    copyBtn.lineStyle(2, 0x8892B0, 1);
    copyBtn.strokeRoundedRect(cx + 10, cy + 48, 130, 44, 10);
    const copyLabel = this.add.text(cx + 75, cy + 70, 'Copy Raw Save', {
      font: 'bold 12px "Space Grotesk"',
      color: '#F5F7FA',
    }).setOrigin(0.5).setDepth(303);
    const copyZone = this.add.zone(cx + 75, cy + 70, 130, 44)
      .setInteractive({ useHandCursor: true })
      .setDepth(304);

    const teardown = () => {
      backdrop.destroy();
      panel.destroy();
      title.destroy();
      body.destroy();
      resetBtn.destroy();
      resetLabel.destroy();
      resetZone.destroy();
      copyBtn.destroy();
      copyLabel.destroy();
      copyZone.destroy();
    };

    copyZone.on('pointerup', () => {
      Haptics.selection();
      // Clipboard API may reject (permissions, insecure context). Fall back to
      // a non-interactive textarea + execCommand so the player still gets the
      // bytes even when async clipboard is unavailable.
      const done = (ok: boolean) => {
        copyLabel.setText(ok ? 'Copied ✓' : 'Copy failed');
      };
      try {
        navigator.clipboard?.writeText(raw).then(() => done(true)).catch(() => done(false));
      } catch {
        done(false);
      }
    });

    resetZone.on('pointerup', () => {
      Haptics.medium();
      this.saveQuotaWarned = false;
      try {
        localStorage.removeItem(SaveManager.LOCAL_SAVE_KEY);
      } catch {
        // Storage may be unavailable; the fresh run will just not persist.
      }
      teardown();
      this.beginFreshRunAfterRecovery();
    });
  }

  /**
   * After the player confirms a corrupt-save reset, pick up the normal
   * fresh-player boot flow: guided tutorial for newcomers, straight-to-game
   * otherwise.
   */
  private beginFreshRunAfterRecovery(): void {
    const tutorialDone = localStorage.getItem('splintr_tutorial') === 'done';
    if (tutorialDone) {
      this.tutorialStep = 6;
      this.beginFreshRun();
    } else {
      this.tutorialStep = 1;
      this.familyProgression.setActiveFamilies(this.familyProgression.getUnlockedFamilies());
      this.preSpawnTutorialStep1();
      this.startTutorial();
    }
  }

  /**
   * Transient warning toast used when a write fails (e.g. quota exceeded).
   * Styled like showDiscoveryToast but with the error accent so the player
   * notices their progress is at risk.
   */
  private showSaveWarningToast(message: string): void {
    const cx = GAME_WIDTH / 2;
    const cy = this.gridOffsetY / 2;
    const bg = this.add.graphics().setDepth(200);
    bg.fillStyle(0x0B0D15, 0.95);
    bg.fillRoundedRect(cx - 160, cy - 24, 320, 48, 10);
    bg.lineStyle(2, 0xFF003C, 0.9);
    bg.strokeRoundedRect(cx - 160, cy - 24, 320, 48, 10);

    const label = this.add.text(cx, cy, message, {
      font: '600 12px Inter',
      color: '#F5F7FA',
    }).setOrigin(0.5).setDepth(201);

    bg.setAlpha(0);
    label.setAlpha(0);
    const items = [bg, label];
    this.tweens.add({
      targets: items,
      alpha: 1,
      duration: 200,
    });
    this.time.delayedCall(3500, () => {
      this.tweens.add({
        targets: items,
        alpha: 0,
        duration: 300,
        onComplete: () => items.forEach((i) => i.destroy()),
      });
    });
  }
}
